import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Pencil, Check, Bike, X, ImageOff, Trash2, FileDown, MessageCircle, ChevronDown, MapPin, Eye } from 'lucide-react';
import { toast } from 'react-toastify';
import { ordersAPI } from '../../config/orders-api';
import { API_CONFIG } from '../../config/api';
import { driversAPI } from '../../config/drivers-api';
import { dispatchAPI } from '../../config/dispatch-api';
import { authAPI } from '../../config/auth-api';
import { helpers } from '../../utils/helpers';
import { ADMIN_PERMISSIONS } from '../../constants/permissions';
import { fetchAddressFromPincode, formatDateOnly, filterEnabledSlots, sortSlotsByTime, normalizeToYYYYMMDD } from '../../utils/utils';
import { formatOrderTotal, formatPrice, formatPaymentType, getSubTotalInputLabel, getDeliveryChargeInputLabel, getDiscountInputLabel, getOrderSubTotal, buildBillAmounts, computePayableTotal, isAdvancePaidPayment, getAdvancePaidAmount, getBalancePaymentAmount, appendNowTimeSlot, isNowTimeSlot } from '../../utils/orderDisplay';
import { formatOrderVariantLabel, orderLineKey, resolveOrderLineImage } from '../../utils/orderProductDisplay';
import {
  getNextOrderStatus,
  getAvailableNegativeStatuses,
  getAvailableNegativeSubStatuses,
  buildNegativeStatusGroupLabel,
  getRecoveryPositiveStatus,
  getRecoveryPositiveStatusLabel,
  getStatusMeta,
  getStatusStepperLabel,
  getStatusSubtext,
  formatStatusSubtextForDisplay,
  isLastOrderStatus,
  isNegativeOrderStatus,
  isLastNegativeOrderStatus,
  requiresStatusReason,
  getStatusReasonField,
  normalizeStatusValue,
  PAYMENT_TYPE_OPTIONS,
  buildShipmentFieldRows,
  splitCustomFieldsForHeader,
  getStatusBadgeStyles,
  getStatusButtonStyles,
  SHIPMENT_FIELD_KEYS,
  SHIPMENT_PARTNER_OPTIONS,
  formatShipmentPartner,
  isShippedStatus,
  formatFieldLabel,
  formatOrderTypeLabel,
  getFlowStatusesForOrder,
} from '../../utils/orderConfigHelpers';
import { buildOrderStatusSnapshot, performOrderStatusUpdate } from '../../utils/orderStatusUpdate';
import { resolveMapUrl, openExternalUrl, buildGoogleMapLink, parseGoogleMapsLink, isShortMapLink } from '../../utils/mapCoordinates';
import { formatPriorityBadgeLabel, normalizePriorityDelTime, splitPriorityDelTime, composePriorityDelTime, PRIORITY_MODES } from '../../utils/priorityDelivery';
import DriverSelectModal from '../Common/DriverSelectModal/DriverSelectModal';
import LocationPickerModal from '../Common/LocationPickerModal/LocationPickerModal';
import DispatchEditModal from '../Pages/DispatchHistory/DispatchEditModal/DispatchEditModal';
import { isIndiaClient, hasStoreFeature } from '../../utils/client';
import { normalizeOptionalWhatsapp, productsRequiredForStore } from '../../utils/orderContact';
import ProductPickerModal from '../Common/ProductPickerModal/ProductPickerModal';
import PincodeSelectModal from '../Common/PincodeSelectModal/PincodeSelectModal';
import '../Settings/OrderSettings.css';
import './Orders.css';

const isMongoId = (value) => /^[a-fA-F0-9]{24}$/.test(String(value || '').trim());
const todayYmd = () => formatDateOnly(new Date());

const mergeSlotsWithSelection = (slots, timeSlotId, timeSlotName) => {
  const list = [...(slots || [])];
  const idStr = timeSlotId ? String(timeSlotId) : '';
  const nameStr = timeSlotName ? String(timeSlotName).trim() : '';
  if (!idStr && !nameStr) return list;

  const exists = list.some(
    (s) => String(s._id) === idStr
      || s.name === nameStr
      || s.name === idStr
      || String(s._id) === nameStr
  );

  if (!exists && nameStr) {
    list.unshift({
      _id: isMongoId(idStr) ? idStr : nameStr,
      name: nameStr,
      startTime: '',
    });
  }
  return list;
};

const cleanQatarPhone = (phone) => {
  if (!phone) return '';
  let cleaned = String(phone).replace(/\D/g, '');
  if (cleaned.startsWith('974')) {
    cleaned = cleaned.slice(3);
  } else if (cleaned.startsWith('00974')) {
    cleaned = cleaned.slice(5);
  }
  return cleaned.slice(0, 8);
};

const cleanIndiaPhone = (phone) => {
  if (!phone) return '';
  let cleaned = String(phone).replace(/\D/g, '');
  if (cleaned.startsWith('91')) {
    cleaned = cleaned.slice(2);
  } else if (cleaned.startsWith('0091')) {
    cleaned = cleaned.slice(4);
  } else if (cleaned.startsWith('0') && cleaned.length === 11) {
    cleaned = cleaned.slice(1);
  }
  return cleaned.slice(0, 10);
};

const getOrderNote = (order) => String(order?.orderNote || order?.notes || '').trim();

const hasOrderProducts = (order) => Array.isArray(order?.products) && order.products.length > 0;

const OrderDetailsModal = ({
  orderId,
  adminData,
  canEdit,
  canUpdateStatus,
  onClose,
  onUpdated,
  onOpenRelatedOrder,
}) => {
  const isIndia = isIndiaClient(adminData);
  const useCourierShipment = hasStoreFeature(adminData, 'courierShipment');
  const useDeliveryBoyShipment = hasStoreFeature(adminData, 'deliveryBoyShipment');
  const useDelhiveryTracking = hasStoreFeature(adminData, 'delhiveryTracking');
  const useReturnOrders = hasStoreFeature(adminData, 'returnOrders');
  const useDispatch = hasStoreFeature(adminData, 'dispatch');
  const usePriorityDelivery = hasStoreFeature(adminData, 'priorityDelivery');
  const hasUpdateStatusPerm = canUpdateStatus !== undefined
    ? canUpdateStatus
    : (canEdit || helpers.hasPermission(adminData, ADMIN_PERMISSIONS.UPDATE_ORDER_STATUS));
  const canEditPaymentType = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.EDIT_PAYMENT_TYPE);
  const canViewAllOrders = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.VIEW_ALL_ORDERS);

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [activeEditSection, setActiveEditSection] = useState(null);
  const [sectionSaving, setSectionSaving] = useState(false);
  const [statusConfirm, setStatusConfirm] = useState({ isOpen: false, status: null, title: '', message: '', reason: '' });
  const [negativeMenuOpen, setNegativeMenuOpen] = useState(false);
  const negativeMenuRef = useRef(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceMenuOpen, setInvoiceMenuOpen] = useState(false);
  const invoiceMenuRef = useRef(null);
  const [creatingReturn, setCreatingReturn] = useState(false);
  const [productPicker, setProductPicker] = useState(null);
  const [cancelPromptOpen, setCancelPromptOpen] = useState(false);
  const [shippedModal, setShippedModal] = useState({ isOpen: false, shipmentNumber: '', shipmentPartner: '' });
  const [deliveryTrackModal, setDeliveryTrackModal] = useState({
    isOpen: false,
    loading: false,
    error: null,
    data: null,
  });
  const [formData, setFormData] = useState(null);
  const [showDriverSelect, setShowDriverSelect] = useState(false);
  const [driverActionLoading, setDriverActionLoading] = useState(false);
  const [driverSelectMode, setDriverSelectMode] = useState('view');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isProductPickerOpen, setIsProductPickerOpen] = useState(false);
  const [isViewProductsOpen, setIsViewProductsOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editProductQty, setEditProductQty] = useState(1);
  const [pincodeOptions, setPincodeOptions] = useState([]);
  const [isPincodeModalOpen, setIsPincodeModalOpen] = useState(false);
  const [deliverySlots, setDeliverySlots] = useState([]);
  const [loadingDeliverySlots, setLoadingDeliverySlots] = useState(false);
  const [isLocationPickerOpen, setIsLocationPickerOpen] = useState(false);
  const [isLocationUpdated, setIsLocationUpdated] = useState(false);
  const [mapLinkDraft, setMapLinkDraft] = useState('');
  const [resolvingMapLink, setResolvingMapLink] = useState(false);
  const mapLinkResolveTimer = useRef(null);

  const prepareProductsForViewEdit = () => {
    if (!order) return [];
    const productsMap = (order.products || []).map((p) => ({
      _id: p._id,
      productId: typeof p.productId === 'number' ? p.productId : null,
      name: p.name,
      price: parseFloat(p.price || p.rate) || 0,
      qty: parseInt(p.qty, 10) || 1,
      images: p.images || [],
      selectedVariant: p.selectedVariant || null,
    }));
    setSelectedProducts(productsMap);
    return productsMap;
  };

  const handleSaveProductsDirectly = async (newSelectedProducts, successMsg = 'Order products updated successfully') => {
    const isStandardOrder = String(order?.type || 'order').toLowerCase() === 'order';
    if (productsRequiredForStore(adminData) && isStandardOrder && !newSelectedProducts.length) {
      toast.error('At least one product is required');
      return;
    }

    try {
      setSubmitting(true);
      const newTotal = newSelectedProducts.reduce((sum, item) => sum + (item.price * item.qty), 0);

      const payload = {
        products: newSelectedProducts.map((p) => ({
          _id: p._id,
          productId: p.productId,
          name: p.name,
          price: parseFloat(p.price) || 0,
          qty: parseInt(p.qty, 10) || 1,
          images: p.images || [],
          selectedVariant: p.selectedVariant || null,
        })),
        bill: buildBillAmounts({
          subTotal: newTotal,
          deliveryCharge: order.bill?.deliveryCharge,
          discount: order.bill?.discount,
        }),
      };

      const result = await ordersAPI.updateOrder(orderId, payload);
      if (result.success) {
        setOrder(result.data);
        toast.success(successMsg);
        onUpdated?.();
      } else {
        toast.error(result.error || 'Failed to update order products');
      }
    } catch {
      toast.error('An error occurred while saving products');
    } finally {
      setSubmitting(false);
    }
  };



  const handleUpdateSelectedQty = (productId, variantSku, newQty) => {
    const qty = Math.max(1, parseInt(newQty, 10) || 1);
    setSelectedProducts((prev) =>
      prev.map((item) => {
        const matchId = item._id === productId;
        const matchVariant = (item.selectedVariant?.sku || null) === (variantSku || null);
        return matchId && matchVariant ? { ...item, qty } : item;
      })
    );
  };

  const itemTotal = selectedProducts.reduce((sum, item) => sum + (item.price * item.qty), 0);

  useEffect(() => {
    if (selectedProducts.length > 0) {
      setFormData((prev) => (prev ? {
        ...prev,
        totalPrice: itemTotal.toFixed(2),
      } : prev));
    }
  }, [selectedProducts, itemTotal]);

  const [showRouteEditModal, setShowRouteEditModal] = useState(false);
  const [routeEntry, setRouteEntry] = useState(null);
  const [routeDrivers, setRouteDrivers] = useState([]);
  const [routeStoreCoordinates, setRouteStoreCoordinates] = useState(null);
  const [loadingRoute, setLoadingRoute] = useState(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const loadOrder = useCallback(async ({ silent = false } = {}) => {
    if (!orderId) return;
    if (!silent) {
      setLoading(true);
    }
    try {
      const result = await ordersAPI.getOrderDetail(orderId);
      if (result.success) {
        setOrder(result.data);
      } else {
        toast.error(result.error || 'Failed to load order');
        if (!silent) {
          onCloseRef.current();
        }
      }
    } catch {
      toast.error('Failed to load order');
      if (!silent) {
        onCloseRef.current();
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [orderId]);

  useEffect(() => {
    loadOrder();
    setNegativeMenuOpen(false);
    // Only reload when opening a different order — not when parent re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useEffect(() => {
    if (!negativeMenuOpen) return undefined;
    const handleClickOutside = (event) => {
      if (negativeMenuRef.current && !negativeMenuRef.current.contains(event.target)) {
        setNegativeMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [negativeMenuOpen]);

  useEffect(() => {
    driversAPI.getDriversList({ page: 1, limit: 100 }).then((res) => {
      if (res.success) setDrivers(res.data || []);
    });
  }, []);

  const loadAdmins = useCallback(async () => {
    if (admins.length > 0) return;
    setLoadingAdmins(true);
    try {
      const res = await authAPI.getAdminsList(1, 100);
      if (res.success) setAdmins(res.data || []);
    } finally {
      setLoadingAdmins(false);
    }
  }, [admins.length]);

  useEffect(() => {
    if (!invoiceMenuOpen) return undefined;
    const handleClickOutside = (event) => {
      if (invoiceMenuRef.current && !invoiceMenuRef.current.contains(event.target)) {
        setInvoiceMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [invoiceMenuOpen]);

  useEffect(() => {
    setMapLinkDraft(order?.mapLink || '');
  }, [order?._id, order?.mapLink]);

  const startEdit = () => {
    if (!order) return;
    setActiveEditSection(null);
    const addr = order.deliveryAddress || {};
    const deliveryDate = normalizeToYYYYMMDD(order.deliveryDate) || todayYmd();
    const timeSlotId = order.timeSlotId ? String(order.timeSlotId) : '';
    const timeSlotName = order.timeSlot || '';
    setSelectedProducts((order.products || []).map((p) => ({
      _id: p._id,
      productId: typeof p.productId === 'number' ? p.productId : null,
      name: p.name,
      price: parseFloat(p.price || p.rate) || 0,
      qty: parseInt(p.qty, 10) || 1,
      images: p.images || [],
      selectedVariant: p.selectedVariant || null,
    })));
    setFormData({
      customerName: addr.receiversName || '',
      customerPhone: isIndia ? cleanIndiaPhone(addr.receiversPhoneNUmber || '') : cleanQatarPhone(addr.receiversPhoneNUmber || ''),
      customerWhatsapp: addr.receiversWhatsappNumber || '',
      zone: addr.zone || '',
      address: addr.address || '',
      buildingNumber: addr.buildingNumber || addr.houseNo || '',
      apartment: addr.apartment || '',
      directionToReach: addr.directionToReach || '',
      pincode: addr.pincode || '',
      state: addr.state || '',
      district: addr.district || '',
      country: addr.country || '',
      city: addr.city || '',
      lat: addr.lat || '',
      lng: addr.long || addr.lng || '',
      mapLink: order.mapLink || '',
      placeName: addr.address || '',
      driverId: order.driverId?._id || order.driverId || '',
      deliveryDate,
      timeSlot: timeSlotName,
      timeSlotId,
      isPriorityDelivery: Boolean(splitPriorityDelTime(order.priority_del_time).time),
      priorityMode: splitPriorityDelTime(order.priority_del_time).mode,
      priority_del_time: splitPriorityDelTime(order.priority_del_time).time,
      priorityEndTime: splitPriorityDelTime(order.priority_del_time).endTime,
      totalPrice: String(getOrderSubTotal(order.bill)),
      deliveryCharge: order.bill?.deliveryCharge != null ? String(order.bill.deliveryCharge) : '',
      discount: order.bill?.discount != null ? String(order.bill.discount) : '',
      paymentType: order.paymentType || '',
      advancePaidAmount: order.advancePaidAmount != null ? String(order.advancePaidAmount) : '',
      customFields: { ...(order.customFields || {}) },
    });
    setIsLocationUpdated(false);
    loadDeliverySlots(deliveryDate, timeSlotId, timeSlotName);
    setEditMode(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'deliveryDate') {
      setFormData((prev) => ({
        ...prev,
        deliveryDate: value,
        timeSlot: '',
        timeSlotId: '',
      }));
      loadDeliverySlots(value);
      return;
    }
    if (name === 'timeSlot') {
      const slot = deliverySlots.find((s) => s.name === value);
      setFormData((prev) => ({
        ...prev,
        timeSlot: value,
        timeSlotId: slot?._id && slot._id !== 'now' ? String(slot._id) : '',
      }));
      return;
    }
    if (name === 'paymentType') {
      setFormData((prev) => ({
        ...prev,
        paymentType: value,
        advancePaidAmount: value === 'advance_paid' ? prev.advancePaidAmount : '',
      }));
      return;
    }
    if (name === 'customerPhone') {
      const cleaner = isIndia ? cleanIndiaPhone : cleanQatarPhone;
      setFormData((prev) => ({ ...prev, customerPhone: cleaner(value) }));
      return;
    }
    if (name === 'pincode') {
      const val = value.replace(/\D/g, '').slice(0, 6);
      setFormData((prev) => ({ ...prev, pincode: val }));

      if (isIndia && val.length === 6) {
        fetchAddressFromPincode(val)
          .then((address) => {
            if (address) {
              setFormData((prev) => ({ ...prev, ...address }));
              toast.success('Address details fetched from pincode');
            } else {
              toast.error('Invalid pincode or details not found');
            }
          })
          .catch((err) => {
            console.error('Error fetching pincode details', err);
            toast.error('Error fetching pincode details');
          });
      }
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneBlur = (e) => {
    const value = e.target.value.trim();
    if (!value) {
      toast.error('Customer phone is required');
    } else if (isIndia) {
      if (value.length !== 10) toast.error('Phone number must be exactly 10 digits');
    } else if (!/^\d{8}$/.test(value)) {
      toast.error('Phone number must be exactly 8 digits');
    }
  };

  const handlePincodeBlur = (e) => {
    const value = e.target.value.trim();
    if (!value) {
      toast.error('Pincode is required');
    } else if (!/^\d{6}$/.test(value)) {
      toast.error('Pincode must be 6 digits');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData) return;

    setSubmitting(true);
    try {
      if (!formData.customerName.trim()) {
        toast.error('Customer name is required');
        return;
      }
      const localPhone = formData.customerPhone.trim();
      if (!localPhone) {
        toast.error('Customer phone is required');
        return;
      }
      if (isIndia) {
        if (localPhone.length !== 10) {
          toast.error('Phone number must be exactly 10 digits');
          return;
        }
      } else if (!/^\d{8}$/.test(localPhone)) {
        toast.error('Phone number must be exactly 8 digits');
        return;
      }
      if (!isIndia && !formData.zone.trim()) {
        toast.error('Zone number is required');
        return;
      }
      if (isIndia && !/^\d{6}$/.test(formData.pincode.trim())) {
        toast.error('Pincode must be 6 digits');
        return;
      }
      if (!formData.deliveryDate || !formData.timeSlot) {
        toast.error('Delivery date and time slot are required');
        return;
      }
      if (usePriorityDelivery && formData.isPriorityDelivery) {
        const priorityCheck = normalizePriorityDelTime(
          composePriorityDelTime(formData.priorityMode, formData.priority_del_time, formData.priorityEndTime),
          { enabled: true }
        );
        if (!priorityCheck.success) {
          toast.error(priorityCheck.msg || 'Select a priority delivery time');
          return;
        }
      }

      const fieldConfig = order?.orderConfig?.orderCustomFields || [];
      for (const field of fieldConfig) {
        if (field.required && !String(formData.customFields?.[field.key] || '').trim()) {
          toast.error(`${field.label} is required`);
          return;
        }
      }

      const isStandardOrder = String(order?.type || 'order').toLowerCase() === 'order';
      if (productsRequiredForStore(adminData) && isStandardOrder && selectedProducts.length === 0) {
        toast.error('At least one product is required');
        return;
      }

      const fullPhone = isIndia ? `+91${localPhone}` : `+974${localPhone}`;
      const selectedSlot = deliverySlots.find((s) => s.name === formData.timeSlot);
      const slotId = isMongoId(selectedSlot?._id)
        ? String(selectedSlot._id)
        : (isMongoId(formData.timeSlotId) ? formData.timeSlotId : null);

      const payload = {
        customerName: formData.customerName.trim(),
        customerPhone: fullPhone,
        receiversWhatsappNumber: normalizeOptionalWhatsapp(formData.customerWhatsapp),
        zone: isIndia ? '' : formData.zone.trim(),
        address: formData.address.trim(),
        buildingNumber: formData.buildingNumber.trim(),
        apartment: formData.apartment.trim(),
        directionToReach: formData.directionToReach.trim(),
        pincode: formData.pincode ? formData.pincode.trim() : '',
        state: formData.state ? formData.state.trim() : '',
        district: formData.district ? formData.district.trim() : '',
        country: formData.country ? formData.country.trim() : '',
        city: formData.city ? formData.city.trim() : '',
        mapLink: (formData.lat && formData.lng)
          ? buildGoogleMapLink(formData.lat, formData.lng)
          : String(formData.mapLink || '').trim(),
        driverId: formData.driverId || null,
        bill: buildBillAmounts({
          subTotal: selectedProducts.length > 0 ? itemTotal : (parseFloat(formData.totalPrice) || 0),
          deliveryCharge: formData.deliveryCharge,
          discount: formData.discount,
        }),
        products: selectedProducts.map((p) => ({
          _id: p._id,
          productId: p.productId,
          name: p.name,
          price: parseFloat(p.price) || 0,
          qty: parseInt(p.qty, 10) || 1,
          images: p.images || [],
          selectedVariant: p.selectedVariant || null,
        })),
        paymentType: formData.paymentType || '',
        advancePaidAmount: formData.paymentType === 'advance_paid'
          ? (parseFloat(formData.advancePaidAmount) || 0)
          : 0,
        customFields: formData.customFields || {},
        deliveryDate: formatDateOnly(formData.deliveryDate),
        timeSlot: formData.timeSlot,
        timeSlotId: isNowTimeSlot(formData.timeSlot) ? null : (slotId || null),
      };
      if (usePriorityDelivery) {
        payload.isPriorityDelivery = Boolean(formData.isPriorityDelivery);
        payload.priority_del_time = formData.isPriorityDelivery
          ? composePriorityDelTime(formData.priorityMode, formData.priority_del_time, formData.priorityEndTime)
          : '';
      }
      if (formData.lat) payload.lat = String(formData.lat).trim();
      if (formData.lng) payload.lng = String(formData.lng).trim();

      const result = await ordersAPI.updateOrder(orderId, payload);
      if (result.success) {
        setOrder(result.data);
        toast.success('Order updated successfully');
        setEditMode(false);
        setFormData(null);
        setIsLocationUpdated(false);
        onUpdated?.();
      } else {
        toast.error(result.error || 'Failed to update order');
      }
    } catch {
      toast.error('Failed to update order');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAdvanceStatus = async (nextStatus, isConfirmed = false, reason = '') => {
    const statusValue = normalizeStatusValue(nextStatus);
    if (!statusValue) return;

    const flow = getFlowStatusesForOrder(order, order?.orderConfig || {});
    const flowOrderStatuses = flow.orderStatuses;
    const flowNegativeStatuses = flow.negativeOrderStatuses;

    if (!isConfirmed) {
      if (isShippedStatus(statusValue) && (useCourierShipment || useDeliveryBoyShipment)) {
        setShippedModal({
          isOpen: true,
          shipmentNumber: order?.customFields?.[SHIPMENT_FIELD_KEYS.number] || '',
          shipmentPartner: order?.customFields?.[SHIPMENT_FIELD_KEYS.partner] || '',
        });
        return;
      }
      if (requiresStatusReason(statusValue)) {
        const meta = getStatusMeta(statusValue, flowOrderStatuses, flowNegativeStatuses);
        setStatusConfirm({
          isOpen: true,
          status: statusValue,
          title: `Update to ${meta.label}`,
          message: `Add an optional reason for marking this order as ${meta.label.toLowerCase()}.`,
          reason: '',
        });
        return;
      }
      const meta = getStatusMeta(statusValue, flowOrderStatuses, flowNegativeStatuses);
      setStatusConfirm({
        isOpen: true,
        status: statusValue,
        title: `Update to ${meta.label}`,
        message: `Are you sure you want to mark this order as ${meta.label.toLowerCase()}?`,
        reason: '',
      });
      return;
    }

    setStatusConfirm({ isOpen: false, status: null, title: '', message: '', reason: '' });
    setStatusUpdating(true);
    try {
      const extras = {};
      const reasonField = getStatusReasonField(statusValue);
      if (reasonField) extras[reasonField] = reason;
      const snapshot = buildOrderStatusSnapshot(order);

      await performOrderStatusUpdate({
        orderId,
        newStatus: statusValue,
        snapshot,
        orderStatuses: flowOrderStatuses,
        negativeOrderStatuses: flowNegativeStatuses,
        extras,
        onSuccess: async () => {
          await loadOrder({ silent: true });
          onUpdated?.();
        },
      });
    } catch {
      toast.error('Failed to update order status');
    } finally {
      setStatusUpdating(false);
    }
  };

  const openProductPicker = (kind) => {
    const lines = (order?.products || []).map((product) => ({
      _id: product._id,
      productId: product.productId,
      name: product.name || 'Product',
      price: Number(product.price) || 0,
      maxQty: Number(product.qty) || 1,
      qty: Number(product.qty) || 1,
      selected: true,
    }));
    setProductPicker({ kind, lines });
  };

  const submitProductPicker = async () => {
    if (!orderId || !productPicker || creatingReturn) return;
    const products = productPicker.lines
      .filter((line) => line.selected && Number(line.qty) > 0)
      .map((line) => ({
        _id: line._id,
        productId: line.productId,
        qty: Math.min(Number(line.qty) || 0, line.maxQty),
      }));
    if (!products.length) {
      toast.error('Select at least one product');
      return;
    }
    setCreatingReturn(true);
    try {
      const result = await ordersAPI.createReturnOrder(orderId, {
        type: productPicker.kind,
        products,
      });
      if (result.success) {
        const label = productPicker.kind === 'replacement' ? 'Replacement' : 'Return';
        toast.success(`${label} order #${result.data?.orderId || ''} created`);
        setProductPicker(null);
        onUpdated?.();
        const newId = result.data?._id;
        if (newId && onOpenRelatedOrder) {
          onOpenRelatedOrder(newId);
        } else {
          await loadOrder({ silent: true });
        }
      } else {
        toast.error(result.error || 'Failed to create order');
      }
    } catch {
      toast.error('Failed to create order');
    } finally {
      setCreatingReturn(false);
    }
  };

  const confirmCancelRelated = async (nextStatus) => {
    if (!orderId) return;
    setStatusUpdating(true);
    try {
      const result = await ordersAPI.cancelRelatedOrder(orderId, nextStatus);
      if (result.success) {
        toast.success(nextStatus === 'cancelled' ? 'Order cancelled' : 'Order set back to delivered');
        setCancelPromptOpen(false);
        await loadOrder({ silent: true });
        onUpdated?.();
      } else {
        toast.error(result.error || 'Failed to update the order');
      }
    } catch {
      toast.error('Failed to update the order');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleCreateReturnOrder = async () => {
    openProductPicker('return');
  };

  const handleShippedSubmit = async () => {
    const shipmentNumber = shippedModal.shipmentNumber.trim();
    const shipmentPartner = shippedModal.shipmentPartner;

    if (useCourierShipment) {
      if (!shipmentNumber) {
        toast.error('Shipment number is required');
        return;
      }
      if (!shipmentPartner) {
        toast.error('Shipment partner is required');
        return;
      }
    } else if (useDeliveryBoyShipment) {
      if (!shipmentPartner) {
        toast.error('Delivery boy is required');
        return;
      }
    }

    const flow = getFlowStatusesForOrder(order, order?.orderConfig || {});

    setStatusUpdating(true);
    try {
      const customFields = { ...(order.customFields || {}) };
      if (useCourierShipment) {
        customFields[SHIPMENT_FIELD_KEYS.number] = shipmentNumber;
        customFields[SHIPMENT_FIELD_KEYS.partner] = shipmentPartner;
      } else if (useDeliveryBoyShipment) {
        customFields[SHIPMENT_FIELD_KEYS.partner] = shipmentPartner;
      }

      const updateResult = await ordersAPI.updateOrder(orderId, { customFields });
      if (!updateResult.success) {
        toast.error(updateResult.error || 'Failed to save shipment details');
        return;
      }

      const result = await ordersAPI.updateOrderStatus(orderId, 'shipped');
      if (result.success) {
        toast.success(`Order status updated to ${getStatusMeta('shipped', flow.orderStatuses, flow.negativeOrderStatuses).label}`);
        setShippedModal({ isOpen: false, shipmentNumber: '', shipmentPartner: '' });
        await loadOrder({ silent: true });
        onUpdated?.();
      } else {
        toast.error(result.error || 'Failed to update order status');
      }
    } catch {
      toast.error('Failed to update order status');
    } finally {
      setStatusUpdating(false);
    }
  };

  const loadDeliverySlots = useCallback(async (dateValue, selectedSlotId = '', selectedSlotName = '') => {
    if (!dateValue) {
      setDeliverySlots([]);
      return;
    }
    setLoadingDeliverySlots(true);
    try {
      const result = await dispatchAPI.getDispatchDetails(dateValue);
      const apiSlots = result.success
        ? sortSlotsByTime(filterEnabledSlots(result.data?.timeSlots || []))
        : [];
      const withNow = appendNowTimeSlot(apiSlots, dateValue);
      setDeliverySlots(mergeSlotsWithSelection(withNow, selectedSlotId, selectedSlotName));
    } catch {
      setDeliverySlots(mergeSlotsWithSelection([], selectedSlotId, selectedSlotName));
    } finally {
      setLoadingDeliverySlots(false);
    }
  }, []);

  const startSectionEdit = (section) => {
    if (!order) return;
    if (section === 'customer') {
      const address = order.deliveryAddress || {};
      setFormData({
        customerName: address.receiversName || '',
        customerPhone: isIndia ? cleanIndiaPhone(address.receiversPhoneNUmber || '') : cleanQatarPhone(address.receiversPhoneNUmber || ''),
        customerWhatsapp: address.receiversWhatsappNumber || '',
        zone: address.zone || '',
        address: address.address || '',
        buildingNumber: address.buildingNumber || address.houseNo || '',
        apartment: address.apartment || '',
        directionToReach: address.directionToReach || '',
        pincode: address.pincode || '',
        state: address.state || '',
        district: address.district || '',
        country: address.country || '',
        city: address.city || '',
        lat: address.lat || '',
        lng: address.long || address.lng || '',
        mapLink: order.mapLink || '',
        placeName: address.address || '',
      });
      setIsLocationUpdated(false);
    } else if (section === 'billing') {
      setFormData({
        totalPrice: String(getOrderSubTotal(order.bill)),
        deliveryCharge: order.bill?.deliveryCharge != null ? String(order.bill.deliveryCharge) : '',
        discount: order.bill?.discount != null ? String(order.bill.discount) : '',
        paymentType: order.paymentType || '',
        advancePaidAmount: order.advancePaidAmount != null ? String(order.advancePaidAmount) : '',
      });
    } else if (section === 'custom') {
      const fieldConfig = order?.orderConfig?.orderCustomFields || [];
      const draft = {};
      fieldConfig.forEach((field) => {
        draft[field.key] = order?.customFields?.[field.key] ?? '';
      });
      setFormData({ customFields: draft });
    } else if (section === 'shipment') {
      setFormData({
        customFields: {
          [SHIPMENT_FIELD_KEYS.number]: order?.customFields?.[SHIPMENT_FIELD_KEYS.number] || '',
          [SHIPMENT_FIELD_KEYS.partner]: order?.customFields?.[SHIPMENT_FIELD_KEYS.partner] || '',
        },
      });
    } else if (section === 'driver') {
      setFormData({
        driverId: order.driverId?._id || order.driverId || '',
      });
    } else if (section === 'note') {
      setFormData({ orderNote: getOrderNote(order) });
    } else if (section === 'comments') {
      setFormData({ comments: order?.comments || '' });
    } else if (section === 'delivery') {
      const dateValue = order.deliveryDateValue || normalizeToYYYYMMDD(order.deliveryDate) || todayYmd();
      const timeSlotId = order.timeSlotId ? String(order.timeSlotId) : '';
      const timeSlotName = order.timeSlot || '';
      const priorityParts = splitPriorityDelTime(order.priority_del_time);
      setFormData({
        deliveryDate: dateValue,
        timeSlot: timeSlotName,
        timeSlotId,
        isPriorityDelivery: Boolean(priorityParts.time),
        priorityEndTime: priorityParts.endTime,
        priorityMode: priorityParts.mode,
        priority_del_time: priorityParts.time,
      });
      loadDeliverySlots(dateValue, timeSlotId, timeSlotName);
    } else if (section === 'admin') {
      setFormData({ adminId: order.adminId ? String(order.adminId) : '' });
      loadAdmins();
    } else if (section === 'location') {
      const addr = order.deliveryAddress || {};
      setFormData({
        lat: addr.lat || '',
        lng: addr.long || addr.lng || '',
        mapLink: order.mapLink || '',
        placeName: addr.address || '',
      });
      setIsLocationUpdated(false);
    }
    setActiveEditSection(section);
  };

  const cancelSectionEdit = () => {
    setActiveEditSection(null);
    setFormData(null);
  };

  const saveSectionEdit = async () => {
    if (!formData || !activeEditSection) return;

    setSectionSaving(true);
    try {
      let payload = {};

      if (activeEditSection === 'customer') {
        if (!formData.customerName.trim()) {
          toast.error('Customer name is required');
          return;
        }
        const localPhone = formData.customerPhone.trim();
        if (!localPhone) {
          toast.error('Customer phone is required');
          return;
        }
        if (isIndia) {
          if (localPhone.length !== 10) {
            toast.error('Phone number must be exactly 10 digits');
            return;
          }
          if (!/^\d{6}$/.test(formData.pincode.trim())) {
            toast.error('Pincode must be 6 digits');
            return;
          }
        } else if (!/^\d{8}$/.test(localPhone)) {
          toast.error('Phone number must be exactly 8 digits');
          return;
        }
        if (!isIndia && !formData.zone.trim()) {
          toast.error('Zone number is required');
          return;
        }
        const fullPhone = isIndia ? `+91${localPhone}` : `+974${localPhone}`;
        payload = {
          ...payload,
          customerName: formData.customerName.trim(),
          customerPhone: fullPhone,
          receiversWhatsappNumber: normalizeOptionalWhatsapp(formData.customerWhatsapp),
          zone: isIndia ? '' : formData.zone.trim(),
          address: formData.address.trim(),
          buildingNumber: formData.buildingNumber.trim(),
          apartment: formData.apartment.trim(),
          directionToReach: formData.directionToReach.trim(),
          pincode: formData.pincode ? formData.pincode.trim() : '',
          state: formData.state ? formData.state.trim() : '',
          district: formData.district ? formData.district.trim() : '',
          country: formData.country ? formData.country.trim() : '',
          city: formData.city ? formData.city.trim() : '',
          mapLink: (formData.lat && formData.lng)
            ? buildGoogleMapLink(formData.lat, formData.lng)
            : String(formData.mapLink || '').trim(),
        };
        if (formData.lat) payload.lat = String(formData.lat).trim();
        if (formData.lng) payload.lng = String(formData.lng).trim();
      } else if (activeEditSection === 'billing') {
        if (formData.paymentType === 'advance_paid') {
          const advanceAmount = Number(formData.advancePaidAmount);
          if (!Number.isFinite(advanceAmount) || advanceAmount <= 0) {
            toast.error('Advance amount is required for Advance Payed orders');
            return;
          }
        }
        payload = {
          ...payload,
          bill: buildBillAmounts({
            subTotal: parseFloat(formData.totalPrice) || 0,
            deliveryCharge: formData.deliveryCharge,
            discount: formData.discount,
          }),
          paymentType: formData.paymentType || '',
          advancePaidAmount: formData.paymentType === 'advance_paid'
            ? (parseFloat(formData.advancePaidAmount) || 0)
            : 0,
        };
      } else if (activeEditSection === 'custom') {
        const fieldConfig = order?.orderConfig?.orderCustomFields || [];
        for (const field of fieldConfig) {
          if (field.required && !String(formData.customFields?.[field.key] || '').trim()) {
            toast.error(`${field.label} is required`);
            return;
          }
        }
        payload = {
          ...payload,
          customFields: {
            ...(order.customFields || {}),
            ...(formData.customFields || {}),
          },
        };
      } else if (activeEditSection === 'shipment') {
        const customFields = { ...(order.customFields || {}) };
        if (useCourierShipment) {
          customFields[SHIPMENT_FIELD_KEYS.number] = String(formData.customFields?.[SHIPMENT_FIELD_KEYS.number] || '').trim();
          customFields[SHIPMENT_FIELD_KEYS.partner] = String(formData.customFields?.[SHIPMENT_FIELD_KEYS.partner] || '').trim();
        } else if (useDeliveryBoyShipment) {
          customFields[SHIPMENT_FIELD_KEYS.partner] = String(formData.customFields?.[SHIPMENT_FIELD_KEYS.partner] || '').trim();
        }
        payload = { ...payload, customFields };
      } else if (activeEditSection === 'driver') {
        payload = {
          ...payload,
          driverId: formData.driverId || null,
        };
      } else if (activeEditSection === 'note') {
        payload = {
          ...payload,
          orderNote: String(formData.orderNote || '').trim(),
        };
      } else if (activeEditSection === 'comments') {
        payload = {
          ...payload,
          comments: String(formData.comments || '').trim(),
        };
      } else if (activeEditSection === 'delivery') {
        if (!formData.deliveryDate) {
          toast.error('Delivery date is required');
          return;
        }
        if (!formData.timeSlot) {
          toast.error('Time slot is required');
          return;
        }
        if (usePriorityDelivery && formData.isPriorityDelivery) {
          const priorityCheck = normalizePriorityDelTime(
          composePriorityDelTime(formData.priorityMode, formData.priority_del_time, formData.priorityEndTime),
          { enabled: true }
        );
          if (!priorityCheck.success) {
            toast.error(priorityCheck.msg || 'Select a priority delivery time');
            return;
          }
        }
        payload = {
          ...payload,
          deliveryDate: formData.deliveryDate,
          timeSlot: formData.timeSlot,
          timeSlotId: formData.timeSlotId || null,
        };
        if (usePriorityDelivery) {
          payload.isPriorityDelivery = Boolean(formData.isPriorityDelivery);
          payload.priority_del_time = formData.isPriorityDelivery
          ? composePriorityDelTime(formData.priorityMode, formData.priority_del_time, formData.priorityEndTime)
          : '';
        }
      } else if (activeEditSection === 'admin') {
        if (!formData.adminId) {
          toast.error('Please select an admin');
          return;
        }
        payload = {
          ...payload,
          adminId: formData.adminId,
        };
      } else if (activeEditSection === 'location') {
        payload = {
          ...payload,
          lat: formData.lat || '',
          lng: formData.lng || '',
          mapLink: formData.mapLink || '',
        };
      }

      const result = await ordersAPI.updateOrder(orderId, payload);
      if (result.success) {
        setOrder(result.data);
        toast.success('Order updated successfully');
        cancelSectionEdit();
        onUpdated?.();
      } else {
        toast.error(result.error || 'Failed to update order');
      }
    } catch {
      toast.error('Failed to update order');
    } finally {
      setSectionSaving(false);
    }
  };

  const handleLocationPicked = (location) => {
    setFormData((prev) => prev ? {
      ...prev,
      lat: String(location.lat),
      lng: String(location.lng),
      mapLink: location.mapLink || '',
      placeName: location.placeName || prev.placeName,
      address: location.placeName || prev.address,
    } : prev);
    setMapLinkDraft(location.mapLink || '');
    setIsLocationUpdated(true);
  };

  const locationDraftIsOpen = Boolean(
    formData && (editMode || activeEditSection === 'customer' || activeEditSection === 'location')
  );

  const commitMapLocation = async ({ lat, lng, mapLink }) => {
    const nextLink = mapLink || (lat && lng ? buildGoogleMapLink(lat, lng) : '');
    setMapLinkDraft(nextLink);
    if (locationDraftIsOpen) {
      handleLocationPicked({ lat, lng, mapLink: nextLink, placeName: '' });
      return;
    }
    if (!lat || !lng) return;
    const result = await ordersAPI.updateOrder(orderId, {
      lat: String(lat),
      lng: String(lng),
      mapLink: nextLink,
    });
    if (result.success) {
      setOrder(result.data);
      toast.success('Map location saved');
      onUpdated?.();
    } else {
      toast.error(result.error || 'Failed to save location');
    }
  };

  const applyEditedMapLink = (rawLink) => {
    const mapLink = String(rawLink || '');
    setMapLinkDraft(mapLink);
    const parsed = parseGoogleMapsLink(mapLink);
    if (mapLinkResolveTimer.current) {
      clearTimeout(mapLinkResolveTimer.current);
      mapLinkResolveTimer.current = null;
    }
    if (parsed) {
      commitMapLocation({
        lat: parsed.lat,
        lng: parsed.lng,
        mapLink: buildGoogleMapLink(parsed.lat, parsed.lng),
      });
      return;
    }
    const trimmed = mapLink.trim();
    const needsResolve = Boolean(trimmed) && (
      isShortMapLink(trimmed) || /google\.[^/\s]+\/maps|maps\.google|goo\.gl/i.test(trimmed)
    );
    if (!needsResolve) return;
    mapLinkResolveTimer.current = setTimeout(async () => {
      setResolvingMapLink(true);
      try {
        const result = await ordersAPI.extractCoordinates(trimmed);
        if (result.success && result.coordinates) {
          await commitMapLocation({
            lat: result.coordinates.lat,
            lng: result.coordinates.lng,
            mapLink: trimmed,
          });
        } else {
          toast.error(result.error || 'Could not resolve this map link.');
        }
      } catch {
        toast.error('Could not resolve this map link.');
      } finally {
        setResolvingMapLink(false);
      }
    }, 400);
  };

  const openMapUrl = () => {
    if (!formData) return;
    const url = resolveMapUrl({
      mapLink: formData.mapLink,
      lat: formData.lat,
      lng: formData.lng,
    });
    if (!url || !openExternalUrl(url)) {
      toast.error('No location available for this order');
    }
  };

  const isReturnOrder = String(order?.type || 'order').toLowerCase() === 'return';
  const isReplacementOrder = String(order?.type || 'order').toLowerCase() === 'replacement';
  const isLinkedOrder = isReturnOrder || isReplacementOrder;
  const flowStatuses = getFlowStatusesForOrder(order, order?.orderConfig || {});
  const activeOrderStatuses = flowStatuses.orderStatuses;
  const activeNegativeStatuses = flowStatuses.negativeOrderStatuses;
  const inNegativeFlow = isNegativeOrderStatus(order?.orderStatus, activeNegativeStatuses);
  const currentStatusMeta = getStatusMeta(order?.orderStatus, activeOrderStatuses, activeNegativeStatuses);
  const currentStatusValue = normalizeStatusValue(order?.orderStatus);
  const isTerminalCancelled = ['replacement_cancelled', 'cancelled'].includes(currentStatusValue);
  const isLockedLinkedOrder = isReplacementOrder && currentStatusValue === 'replacement_cancelled';
  const nextPositiveStatus = inNegativeFlow
    ? null
    : getNextOrderStatus(order?.orderStatus, activeOrderStatuses, activeNegativeStatuses);
  const recoveryPositiveStatus = inNegativeFlow
    ? getRecoveryPositiveStatus(activeOrderStatuses, activeNegativeStatuses)
    : null;
  const recoveryPositiveMeta = getStatusMeta(recoveryPositiveStatus, activeOrderStatuses, activeNegativeStatuses);
  const recoveryPositiveLabel = getRecoveryPositiveStatusLabel(activeOrderStatuses, activeNegativeStatuses);
  const nextPositiveMeta = getStatusMeta(nextPositiveStatus, activeOrderStatuses, activeNegativeStatuses);
  const positiveStepperLabel = getStatusStepperLabel(nextPositiveStatus, activeOrderStatuses, activeNegativeStatuses);
  const statusSubtext = formatStatusSubtextForDisplay(
    getStatusSubtext(order?.orderStatus, activeOrderStatuses, activeNegativeStatuses)
  );
  const availableNegativeStatuses = inNegativeFlow
    ? []
    : getAvailableNegativeStatuses(order?.orderStatus, activeNegativeStatuses);
  const availableNegativeSubStatuses = inNegativeFlow
    ? getAvailableNegativeSubStatuses(order?.orderStatus, activeNegativeStatuses)
    : [];
  const menuNegativeStatuses = availableNegativeStatuses.filter((item) => {
    const value = normalizeStatusValue(item.value);
    if (['returned', 'return', 'replacement'].includes(value)) return false;
    if (currentStatusValue === 'delivered' && value === 'cancelled') return false;
    return true;
  });
  const negativeGroupLabel = buildNegativeStatusGroupLabel(menuNegativeStatuses);
  const isActiveRelatedOrder = (item) => {
    const status = normalizeStatusValue(item?.orderStatus);
    return !['replacement_cancelled', 'cancelled', 'delivered', 'replacement_reached'].includes(status);
  };
  const canOfferLinkedOrders =
    useReturnOrders
    && hasUpdateStatusPerm
    && !isLinkedOrder
    && currentStatusValue === 'delivered';
  const relatedOrders = Array.isArray(order?.relatedOrders) ? order.relatedOrders : [];
  const existingReturn = relatedOrders.find((item) =>
    String(item.type || '').toLowerCase() === 'return' && isActiveRelatedOrder(item)
  );
  const existingReplacement = relatedOrders.find((item) =>
    String(item.type || '').toLowerCase() === 'replacement' && isActiveRelatedOrder(item)
  );
  const cancelledRelatedOrders = relatedOrders.filter((item) =>
    String(item.type || '').toLowerCase() === 'replacement'
    && normalizeStatusValue(item?.orderStatus) === 'replacement_cancelled'
  );
  const hasActiveReplacementOnOriginal =
    !isLinkedOrder
    && Boolean(existingReplacement)
    && ['delivered', 'replacement'].includes(currentStatusValue);
  const canCancelRelated =
    !isLinkedOrder
    && (currentStatusValue === 'returned'
      || currentStatusValue === 'replacement'
      || hasActiveReplacementOnOriginal);
  const cancelRelatedIsReplacement =
    Boolean(existingReplacement)
    && (currentStatusValue === 'replacement' || currentStatusValue === 'delivered');
  const primaryStatusMeta =
    hasActiveReplacementOnOriginal
      ? getStatusMeta('delivered', activeOrderStatuses, activeNegativeStatuses)
      : currentStatusMeta;
  const primaryStatusBadgeStyles = getStatusBadgeStyles(primaryStatusMeta.color);
  const canCancelDeliveredOrder =
    hasUpdateStatusPerm
    && !isLinkedOrder
    && currentStatusValue === 'delivered'
    && !inNegativeFlow
    && !isTerminalCancelled;
  const canCreateReturn = canOfferLinkedOrders;
  const originalOrderRef = order?.originalOrder || null;
  const originalOrderMongoId = originalOrderRef?._id || order?.originalOrderId || null;
  const originalOrderNumber = originalOrderRef?.orderId || null;

  const addr = order?.deliveryAddress || {};

  const openOrderLocation = () => {
    const url = resolveMapUrl({
      mapLink: order?.mapLink,
      lat: addr.lat,
      lng: addr.long || addr.lng,
    });
    if (!url || !openExternalUrl(url)) {
      toast.error('No location available for this order');
    }
  };

  const hasOrderLocation = Boolean(resolveMapUrl({
    mapLink: order?.mapLink,
    lat: addr.lat,
    lng: addr.long || addr.lng,
  }));

  const canUpdateLocation = order
    && !isLockedLinkedOrder
    && !isLastOrderStatus(order.orderStatus, activeOrderStatuses, activeNegativeStatuses)
    && !isTerminalCancelled;

  const orderStatus = (order?.orderStatus || '').toLowerCase();
  const canAssignDriver = canEdit && !isLockedLinkedOrder && orderStatus !== 'delivered' && orderStatus !== 'cancelled' && !isTerminalCancelled;
  const currentDriverId = order?.driverId?._id || order?.driverId || '';

  const openDriverSelect = (mode = 'view') => {
    setDriverSelectMode(mode);
    setShowDriverSelect(true);
  };

  const closeDriverSelect = () => {
    setShowDriverSelect(false);
    setDriverSelectMode('view');
  };

  const handleDriverSelect = async (driver) => {
    if (!driver?._id) return;

    if (driverSelectMode === 'share') {
      handleShareRiderToDriver(driver);
      return;
    }

    if (driverSelectMode === 'edit' && formData) {
      setFormData((prev) => ({ ...prev, driverId: driver._id }));
      closeDriverSelect();
      return;
    }

    setDriverActionLoading(true);
    try {
      const result = await ordersAPI.assignOrderToDriver(orderId, driver._id);
      if (result.success) {
        toast.success('Driver assigned successfully');
        await loadOrder({ silent: true });
        onUpdated?.();
        closeDriverSelect();
      } else {
        toast.error(result.error || 'Failed to assign driver');
      }
    } catch {
      toast.error('Failed to assign driver');
    } finally {
      setDriverActionLoading(false);
    }
  };

  const handleEditInDispatch = async () => {
    if (!order?.dispatchDetails?.routeId) return;
    setLoadingRoute(true);
    try {
      const response = await dispatchAPI.getDispatchHistory(
        1,
        20,
        'today',
        null,
        null,
        order.dispatchDetails.routeId
      );
      if (response.success && response.data) {
        const route = response.data.route || response.data.routes?.[0] || response.data;
        setRouteEntry(route);
        setRouteDrivers(response.data.drivers || []);
        setRouteStoreCoordinates(response.data.storeCoordinates || null);
        setShowRouteEditModal(true);
      } else {
        toast.error(response.msg || response.error || 'Failed to fetch dispatch route details');
      }
    } catch (err) {
      console.error('Error fetching route details for order:', err);
      toast.error('Failed to load dispatch route');
    } finally {
      setLoadingRoute(false);
    }
  };

  const getSelectedDriverName = (driverId) => {
    if (!driverId) return '';
    const match = drivers.find((d) => String(d._id) === String(driverId));
    return match?.name || order?.driverId?.name || '';
  };

  const driverName = order?.staff?.delivery?.name
    || order?.driverId?.name
    || order?.dispatchDetails?.driverName
    || '';
  const { otherFields: headerCustomFieldRows } = splitCustomFieldsForHeader(
    order?.orderConfig?.orderCustomFields,
    order?.customFields
  );
  const shipmentFieldRows = buildShipmentFieldRows(order?.customFields, { courierMode: useCourierShipment });
  const shipmentId = String(order?.customFields?.[SHIPMENT_FIELD_KEYS.number] || '').trim();
  const shipmentPartner = String(order?.customFields?.[SHIPMENT_FIELD_KEYS.partner] || '').trim().toLowerCase();
  const isDelhiveryPartner = shipmentPartner === 'delhivery';
  const canTrackDelhivery = useDelhiveryTracking && Boolean(shipmentId && isDelhiveryPartner);
  const statusBadgeStyles = primaryStatusBadgeStyles;
  const orderNoteText = getOrderNote(order);
  const showInvoice = hasOrderProducts(order);

  const handleDownloadInvoice = async () => {
    setInvoiceLoading(true);
    try {
      const result = await ordersAPI.downloadInvoice(orderId);
      if (!result.success) toast.error(result.error || 'Failed to download invoice');
    } finally {
      setInvoiceLoading(false);
    }
  };

  const openDeliveryTrackModal = async () => {
    if (!canTrackDelhivery) return;
    setDeliveryTrackModal({ isOpen: true, loading: true, error: null, data: null });
    try {
      const result = await ordersAPI.trackDelhivery(shipmentId);
      if (result.success) {
        setDeliveryTrackModal({ isOpen: true, loading: false, error: null, data: result.data });
      } else {
        setDeliveryTrackModal({
          isOpen: true,
          loading: false,
          error: result.error || 'Failed to fetch delivery details',
          data: null,
        });
      }
    } catch {
      setDeliveryTrackModal({
        isOpen: true,
        loading: false,
        error: 'Failed to fetch delivery details',
        data: null,
      });
    }
  };

  const closeDeliveryTrackModal = () => {
    setDeliveryTrackModal({ isOpen: false, loading: false, error: null, data: null });
  };

  const handleShareWhatsApp = () => {
    const phone = String(addr.receiversPhoneNUmber || '').replace(/\D/g, '');
    const total = formatOrderTotal(order?.bill?.totalPrice, adminData);
    const text = [
      `Order #${order?.orderId || ''}`,
      `Status: ${currentStatusMeta.label}`,
      `Total: ${total}`,
      orderNoteText ? `Note: ${orderNoteText}` : '',
    ].filter(Boolean).join('\n');

    let waPhone = '';
    if (phone) {
      if (isIndia) {
        waPhone = phone.startsWith('91') ? phone : `91${phone}`;
      } else {
        waPhone = phone.startsWith('974') ? phone : `974${phone}`;
      }
    }

    const url = waPhone
      ? `https://wa.me/${waPhone}?text=${encodeURIComponent(text)}`
      : `https://wa.me/?text=${encodeURIComponent(text)}`;
    openExternalUrl(url);
  };

  const buildRiderShareUrl = () => {
    if (order?.deliveryUrl || order?.deliveryStaffUrl) {
      return order.deliveryUrl || order.deliveryStaffUrl;
    }
    const base = String(API_CONFIG.STAFF_RIDER_BASE_URL || '').replace(/\/+$/, '');
    const orderMongoId = order?._id || order?.id;
    if (!base || !orderMongoId) return '';
    const driverId = order?.driverId?._id || order?.driverId || order?.staff?.delivery?.id || '';
    let url = `${base}/admin/rider?id=${orderMongoId}&ordersource=app`;
    if (driverId) url += `&rid=${driverId}`;
    return url;
  };

  const buildRiderShareMessage = () => {
    const customerName = addr.receiversName || '';
    const total = formatOrderTotal(order?.bill?.totalPrice, adminData);
    const riderUrl = buildRiderShareUrl();
    const lines = [
      `ORDER #${order?.orderId || ''}`,
      '',
      `To: ${customerName}`,
      `Total: ${total}`,
    ];
    if (riderUrl) lines.push('', riderUrl);
    return lines.join('\n');
  };

  const openWhatsAppShare = (phoneNumber, message) => {
    const encodedText = encodeURIComponent(message);
    const phone = phoneNumber ? String(phoneNumber).replace(/[^\d]/g, '') : '';
    const waUrl = phone
      ? `https://wa.me/${phone}?text=${encodedText}`
      : `https://wa.me/?text=${encodedText}`;
    openExternalUrl(waUrl);
  };

  const openDriverShareModal = () => {
    setDriverSelectMode('share');
    setShowDriverSelect(true);
  };

  const handleShareRiderToDriver = (driver) => {
    if (!driver) return;
    const phone = driver.phoneNumber
      ? `${driver.countryCode || ''}${driver.phoneNumber}`.replace(/\s+/g, '')
      : '';
    openWhatsAppShare(phone, buildRiderShareMessage());
    closeDriverSelect();
  };

  const handleShareRiderToOther = () => {
    openWhatsAppShare(null, buildRiderShareMessage());
    closeDriverSelect();
  };

  return (
    <div className="orderlist-details-overlay" onClick={onClose}>
      <div className="orderlist-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="orderlist-details-header">
          <div className="orderlist-details-header-main">
            <div className="orderlist-details-title-row">
              <h2>{isReturnOrder ? 'Return' : isReplacementOrder ? 'Replacement' : 'Order'} #{order?.orderId || '...'}</h2>
              {order && (
                <span className={`orderlist-type-badge${isReturnOrder ? ' return' : ''}${isReplacementOrder ? ' replacement' : ''}${!isReturnOrder && !isReplacementOrder && String(order?.device?.type || '').toLowerCase() === 'web' ? ' web' : ''}`}>
                  {formatOrderTypeLabel(order.type, order.device)}
                </span>
              )}
              {usePriorityDelivery && formatPriorityBadgeLabel(order?.priority_del_time) && (
                <span className="orderlist-priority-badge">
                  {formatPriorityBadgeLabel(order.priority_del_time)}
                </span>
              )}
            </div>
            {(useReturnOrders || isLinkedOrder) && isLinkedOrder && (originalOrderNumber || originalOrderMongoId) && (
              <div className="orderlist-original-order-row">
                <span>Original order #{originalOrderNumber || '...'}</span>
                {originalOrderMongoId && (
                  <button
                    type="button"
                    className="orderlist-go-to-order-btn"
                    onClick={() => onOpenRelatedOrder?.(originalOrderMongoId)}
                  >
                    Go to order
                  </button>
                )}
              </div>
            )}
            {useReturnOrders && !isReturnOrder && Array.isArray(order?.returnOrders) && order.returnOrders.length > 0 && (
              <div className="orderlist-original-order-row">
                <span>
                  Return{order.returnOrders.length > 1 ? 's' : ''}:{' '}
                  {order.returnOrders.map((item, index) => (
                    <button
                      key={item._id || item.orderId}
                      type="button"
                      className="orderlist-id-link"
                      onClick={() => onOpenRelatedOrder?.(item._id)}
                    >
                      #{item.orderId}{index < order.returnOrders.length - 1 ? ', ' : ''}
                    </button>
                  ))}
                </span>
              </div>
            )}
          </div>
          <div className="orderlist-details-header-actions">
            {order && !editMode && showInvoice && (
              <div className="orderlist-invoice-menu" ref={invoiceMenuRef}>
                <button
                  type="button"
                  className="orderlist-invoice-btn"
                  onClick={() => setInvoiceMenuOpen((open) => !open)}
                  aria-expanded={invoiceMenuOpen}
                  aria-haspopup="menu"
                >
                  <FileDown size={14} />
                  Invoice
                  <ChevronDown size={14} className={`orderlist-invoice-chevron${invoiceMenuOpen ? ' open' : ''}`} />
                </button>
                {invoiceMenuOpen && (
                  <div className="orderlist-invoice-dropdown" role="menu">
                    <button
                      type="button"
                      className="orderlist-invoice-dropdown-item"
                      role="menuitem"
                      onClick={() => {
                        setInvoiceMenuOpen(false);
                        handleDownloadInvoice();
                      }}
                      disabled={invoiceLoading}
                    >
                      <FileDown size={14} />
                      {invoiceLoading ? 'Downloading...' : 'Download PDF'}
                    </button>
                    <button
                      type="button"
                      className="orderlist-invoice-dropdown-item orderlist-invoice-dropdown-whatsapp"
                      role="menuitem"
                      onClick={() => {
                        setInvoiceMenuOpen(false);
                        handleShareWhatsApp();
                      }}
                    >
                      <MessageCircle size={14} />
                      WhatsApp Share
                    </button>
                  </div>
                )}
              </div>
            )}
            <button type="button" className="orderlist-close-btn" onClick={onClose}>&times;</button>
          </div>
        </div>

        {loading ? (
          <div className="orderlist-details-loading">Loading order details...</div>
        ) : editMode && formData ? (
          <form className="orderlist-details-body" onSubmit={handleSave}>
            <div className="orderlist-form-grid">
              <div className="orderlist-form-group">
                <label htmlFor="edit-customerName">Customer Name *</label>
                <input id="edit-customerName" name="customerName" value={formData.customerName} onChange={handleInputChange} required />
              </div>
              <div className="orderlist-form-group">
                <label htmlFor="edit-customerPhone">Customer Phone *</label>
                <div className="orderlist-phone-input-wrapper">
                  <span className="orderlist-phone-prefix">{isIndia ? '+91' : '+974'}</span>
                  <input
                    type="tel"
                    id="edit-customerPhone"
                    name="customerPhone"
                    value={formData.customerPhone}
                    onChange={handleInputChange}
                    onBlur={handlePhoneBlur}
                    maxLength={isIndia ? 10 : 8}
                    required
                  />
                </div>
              </div>
              <div className="orderlist-form-group">
                <label htmlFor="edit-customerWhatsapp">{formatFieldLabel('WhatsApp Number', false)}</label>
                <input
                  type="text"
                  id="edit-customerWhatsapp"
                  name="customerWhatsapp"
                  value={formData.customerWhatsapp || ''}
                  onChange={handleInputChange}
                  placeholder="Optional — include country code if needed"
                  autoComplete="tel"
                />
              </div>
              <div className="orderlist-form-group">
                <label htmlFor="edit-deliveryDate">Delivery Date *</label>
                <input type="date" id="edit-deliveryDate" name="deliveryDate" value={formData.deliveryDate || ''} onChange={handleInputChange} min={todayYmd()} required />
              </div>
              <div className="orderlist-form-group">
                <label htmlFor="edit-timeSlot">Time Slot *</label>
                <select id="edit-timeSlot" name="timeSlot" value={formData.timeSlot || ''} onChange={handleInputChange} required disabled={loadingDeliverySlots}>
                  <option value="">
                    {loadingDeliverySlots
                      ? 'Loading slots...'
                      : deliverySlots.length
                        ? 'Select time slot'
                        : 'No time slots configured'}
                  </option>
                  {deliverySlots.map((slot) => (
                    <option key={slot._id || slot.name} value={slot.name}>{slot.name}</option>
                  ))}
                </select>
                {!loadingDeliverySlots && formData.deliveryDate && deliverySlots.length === 0 && (
                  <p className="orderlist-field-hint orderlist-field-hint-info">
                    No time slots configured. Add slots in admin settings.
                  </p>
                )}
              </div>
              {usePriorityDelivery && (
                <div className="orderlist-form-group orderlist-form-full orderlist-priority-delivery">
                  <label className="orderlist-checkbox-label" htmlFor="edit-isPriorityDelivery">
                    <input
                      type="checkbox"
                      id="edit-isPriorityDelivery"
                      name="isPriorityDelivery"
                      checked={Boolean(formData.isPriorityDelivery)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setFormData((prev) => ({
                          ...prev,
                          isPriorityDelivery: checked,
                          priority_del_time: checked ? prev.priority_del_time : '',
                          priorityEndTime: checked ? prev.priorityEndTime : '',
                        }));
                      }}
                    />
                    Is priority delivery
                  </label>
                  {formData.isPriorityDelivery && (
                    <div className="orderlist-priority-time-row">
                      <label htmlFor="edit-priorityMode">Priority time *</label>
                      <select
                        id="edit-priorityMode"
                        value={PRIORITY_MODES.some((item) => item.value === formData.priorityMode) ? formData.priorityMode : 'before'}
                        onChange={(e) => setFormData((prev) => ({ ...prev, priorityMode: e.target.value }))}
                      >
                        {PRIORITY_MODES.map((item) => (
                          <option key={item.value} value={item.value}>{item.label}</option>
                        ))}
                      </select>
                      <div className="orderlist-priority-times">
                        <input
                          type="time"
                          id="edit-priority_del_time"
                          name="priority_del_time"
                          value={formData.priority_del_time || ''}
                          onChange={handleInputChange}
                          required
                        />
                        {formData.priorityMode === 'between' && (
                          <>
                            <span>to</span>
                            <input
                              type="time"
                              id="edit-priorityEndTime"
                              name="priorityEndTime"
                              value={formData.priorityEndTime || ''}
                              onChange={handleInputChange}
                              required
                            />
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {isIndia && (
                <>
                  <div className="orderlist-form-group">
                    <label htmlFor="edit-pincode">Pincode *</label>
                    <input id="edit-pincode" name="pincode" value={formData.pincode} onChange={handleInputChange} onBlur={handlePincodeBlur} maxLength={6} placeholder="6-digit pincode" required />
                  </div>
                  <div className="orderlist-form-group">
                    <label htmlFor="edit-state">State</label>
                    <input id="edit-state" name="state" value={formData.state} onChange={handleInputChange} placeholder="State" />
                  </div>
                  <div className="orderlist-form-group">
                    <label htmlFor="edit-district">District</label>
                    <input id="edit-district" name="district" value={formData.district} onChange={handleInputChange} placeholder="District" />
                  </div>
                  <div className="orderlist-form-group">
                    <label htmlFor="edit-city">City / Area</label>
                    <input id="edit-city" name="city" value={formData.city} onChange={handleInputChange} placeholder="City / Area" />
                  </div>
                </>
              )}

              {!isIndia && (
                <div className="orderlist-form-group">
                  <label htmlFor="edit-zone">Zone Number *</label>
                  <input id="edit-zone" name="zone" value={formData.zone} onChange={handleInputChange} required />
                </div>
              )}

              <div className="orderlist-form-group">
                <label htmlFor="edit-address">Address</label>
                <input id="edit-address" name="address" value={formData.address} onChange={handleInputChange} />
              </div>
              <div className="orderlist-form-group">
                <label htmlFor="edit-buildingNumber">Building / House No.</label>
                <input id="edit-buildingNumber" name="buildingNumber" value={formData.buildingNumber} onChange={handleInputChange} />
              </div>
              <div className="orderlist-form-group">
                <label htmlFor="edit-apartment">Apartment</label>
                <input id="edit-apartment" name="apartment" value={formData.apartment} onChange={handleInputChange} />
              </div>
              <div className="orderlist-form-group">
                <label>Assign Driver</label>
                {useDispatch && order.dispatchDetails ? (
                  <div className="orderlist-driver-picker-selected orderlist-driver-picker-dispatch">
                    <span className="orderlist-driver-picker-name">{order.dispatchDetails.driverName || getSelectedDriverName(formData.driverId)}</span>
                    <button type="button" className="orderlist-driver-picker-dispatch-btn" onClick={handleEditInDispatch} disabled={loadingRoute}>
                      {loadingRoute ? 'Loading...' : 'Edit in Dispatch'}
                    </button>
                  </div>
                ) : formData.driverId ? (
                  <div className="orderlist-driver-picker-selected">
                    <span className="orderlist-driver-picker-name">{getSelectedDriverName(formData.driverId)}</span>
                    <button type="button" className="orderlist-driver-picker-change" onClick={() => openDriverSelect('edit')}>
                      Change
                    </button>
                  </div>
                ) : (
                  <button type="button" className="orderlist-driver-assign-btn" onClick={() => openDriverSelect('edit')}>
                    Assign Driver
                  </button>
                )}
              </div>
              <div className="orderlist-form-group">
                <label htmlFor="edit-totalPrice">{getSubTotalInputLabel(adminData)}</label>
                <input
                  type="number"
                  step="0.01"
                  id="edit-totalPrice"
                  name="totalPrice"
                  value={formData.totalPrice}
                  onChange={handleInputChange}
                  placeholder="Sub total"
                />
              </div>
              <div className="orderlist-form-group">
                <label htmlFor="edit-deliveryCharge">{getDeliveryChargeInputLabel(adminData)}</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  id="edit-deliveryCharge"
                  name="deliveryCharge"
                  value={formData.deliveryCharge || ''}
                  onChange={handleInputChange}
                  placeholder="Optional"
                />
              </div>
              <div className="orderlist-form-group">
                <label htmlFor="edit-discount">{getDiscountInputLabel(adminData)}</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  id="edit-discount"
                  name="discount"
                  value={formData.discount || ''}
                  onChange={handleInputChange}
                  placeholder="Optional"
                />
              </div>
              <div className="orderlist-form-group orderlist-form-full">
                <p className="orderlist-payable-total">
                  Total Amount:{' '}
                  <strong>
                    {formatOrderTotal(
                      computePayableTotal({
                        subTotal: selectedProducts.length > 0
                          ? itemTotal
                          : (parseFloat(formData.totalPrice) || 0),
                        deliveryCharge: formData.deliveryCharge,
                        discount: formData.discount,
                      }),
                      adminData
                    )}
                  </strong>
                </p>
              </div>
              <div className="orderlist-form-group">
                <label htmlFor="edit-paymentType">Payment Type</label>
                <select
                  id="edit-paymentType"
                  name="paymentType"
                  value={formData.paymentType}
                  onChange={handleInputChange}
                  disabled={!canEditPaymentType}
                >
                  {PAYMENT_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value || 'empty'} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              {formData.paymentType === 'advance_paid' && (
                <div className="orderlist-form-group">
                  <label htmlFor="edit-advancePaidAmount">Advance Amount *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    id="edit-advancePaidAmount"
                    name="advancePaidAmount"
                    value={formData.advancePaidAmount || ''}
                    onChange={handleInputChange}
                    placeholder="Advance amount paid"
                    disabled={!canEditPaymentType}
                  />
                </div>
              )}
              {(order?.orderConfig?.orderCustomFields || []).map((field) => (
                <div className="orderlist-form-group" key={field.key}>
                  <label htmlFor={`edit-custom-${field.key}`}>
                    {formatFieldLabel(field.label, field.required)}
                  </label>
                  <input
                    id={`edit-custom-${field.key}`}
                    value={formData.customFields?.[field.key] || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        customFields: { ...(prev.customFields || {}), [field.key]: value },
                      }));
                    }}
                    required={field.required}
                  />
                </div>
              ))}
              <div className="orderlist-form-group orderlist-form-full">
                <label htmlFor="edit-directionToReach">Directions</label>
                <input id="edit-directionToReach" name="directionToReach" value={formData.directionToReach} onChange={handleInputChange} />
              </div>
              <div className="orderlist-form-group orderlist-form-full">
                <label>{formatFieldLabel('Delivery Location', false)}</label>
                <div className="orderlist-location-row-inline">
                  <button
                    type="button"
                    className="orderlist-view-map-btn"
                    onClick={openMapUrl}
                    disabled={!formData.lat && !formData.lng && !formData.mapLink}
                  >
                    <Eye size={16} /> View Location
                  </button>
                  {canUpdateLocation && (
                    <button
                      type="button"
                      className="orderlist-map-btn"
                      onClick={() => setIsLocationPickerOpen(true)}
                    >
                      <MapPin size={16} /> Update Location
                    </button>
                  )}
                </div>
                {canUpdateLocation && (
                  <div className="orderlist-form-group orderlist-form-full">
                    <label htmlFor="edit-mapLink">Map URL</label>
                    <div className="orderlist-location-row-inline">
                      <input
                        type="text"
                        id="edit-mapLink"
                        value={mapLinkDraft}
                        onChange={(e) => applyEditedMapLink(e.target.value)}
                        placeholder="Paste a Google Maps link"
                        className="orderlist-map-link-input"
                      />
                    </div>
                    {resolvingMapLink && (
                      <div className="orderlist-coords-below">
                        <span>Resolving map link...</span>
                      </div>
                    )}
                  </div>
                )}
                {formData.lat && formData.lng && (
                  <div className="orderlist-coords-below">
                    <Check className="orderlist-coords-success-icon" size={15} aria-hidden />
                    <span>
                      {isLocationUpdated ? 'Coordinates updated: ' : 'Coordinates: '}
                      {formData.lat}, {formData.lng}
                    </span>
                  </div>
                )}
              </div>
              <div className="orderlist-form-group orderlist-form-full orderlist-products-section">
                <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{formatFieldLabel('Products', productsRequiredForStore(adminData) && String(order?.type || 'order').toLowerCase() === 'order')}</span>
                  <button
                    type="button"
                    className="orderlist-add-products-trigger-btn"
                    onClick={() => setIsProductPickerOpen(true)}
                  >
                    Add
                  </button>
                </label>
                {selectedProducts.length > 0 ? (
                  <div className="orderlist-selected-products-summary-card">
                    <div className="orderlist-selected-products-summary-text">
                      {selectedProducts.length} {selectedProducts.length === 1 ? 'product' : 'products'} selected
                    </div>
                    <button
                      type="button"
                      className="orderlist-view-products-toggle-btn"
                      onClick={() => setIsViewProductsOpen(true)}
                    >
                      View Products
                    </button>
                  </div>
                ) : (
                  <div className="orderlist-no-products">No products selected yet.</div>
                )}
              </div>
            </div>
            <div className="orderlist-form-actions">
              <button
                type="button"
                className="orderlist-cancel-btn"
                onClick={() => { setEditMode(false); setFormData(null); setIsLocationUpdated(false); }}
                disabled={submitting}
              >
                Cancel
              </button>
              <button type="submit" className="orderlist-submit-btn" disabled={submitting}>
                {submitting ? 'Updating...' : 'Update Order'}
              </button>
            </div>
          </form>
        ) : order ? (
          <div className="orderlist-details-body">
            <div className="orderlist-details-grid">
              <section className="orderlist-details-card orderlist-status-card">
                <div className="orderlist-status-card-header">
                <h3 className="orderlist-status-card-title">Order Flow</h3>
              </div>
              <div className="orderlist-status-card-body">
                <div className="orderlist-order-status-info">
                  <div className="orderlist-order-status-content">
                    <div className="orderlist-order-status-main orderlist-status-badge-row">
                      <span className="orderlist-status-badge" style={statusBadgeStyles}>
                        {primaryStatusMeta.label}
                      </span>
                      {hasActiveReplacementOnOriginal && existingReplacement && (
                        <button
                          type="button"
                          className="orderlist-type-badge replacement"
                          onClick={() => onOpenRelatedOrder?.(existingReplacement._id)}
                        >
                          Replacement
                        </button>
                      )}
                    </div>
                    {statusSubtext && (
                      <p className="orderlist-order-status-subtext">{statusSubtext}</p>
                    )}
                  </div>
                  {isLockedLinkedOrder ? (
                    <div className="orderlist-order-status-actions orderlist-order-status-actions-locked">
                      <span className="orderlist-status-completed" style={{ color: currentStatusMeta.color }}>
                        <Check size={14} aria-hidden /> No further actions
                      </span>
                      {originalOrderMongoId && (
                        <button
                          type="button"
                          className="orderlist-go-to-order-btn"
                          onClick={() => onOpenRelatedOrder?.(originalOrderMongoId)}
                        >
                          Go to original order
                        </button>
                      )}
                    </div>
                  ) : hasUpdateStatusPerm && (
                    <div className="orderlist-order-status-actions">
                      {inNegativeFlow && recoveryPositiveStatus && availableNegativeSubStatuses.length === 0 && (
                        <button
                          type="button"
                          className="orderlist-action-stepper-btn"
                          style={getStatusButtonStyles(recoveryPositiveMeta.color)}
                          onClick={() => handleAdvanceStatus(recoveryPositiveStatus)}
                          disabled={statusUpdating}
                        >
                          {statusUpdating ? 'Updating...' : recoveryPositiveLabel}
                        </button>
                      )}
                      {!inNegativeFlow && nextPositiveStatus && (
                        <button
                          type="button"
                          className="orderlist-action-stepper-btn"
                          style={getStatusButtonStyles(nextPositiveMeta.color)}
                          onClick={() => handleAdvanceStatus(nextPositiveStatus)}
                          disabled={statusUpdating}
                        >
                          {statusUpdating ? 'Updating...' : positiveStepperLabel}
                        </button>
                      )}
                      {!inNegativeFlow && !nextPositiveStatus && isLastOrderStatus(order.orderStatus, activeOrderStatuses, activeNegativeStatuses) && (
                        <span className="orderlist-status-completed" style={{ color: currentStatusMeta.color }}>
                          <Check size={14} aria-hidden /> {currentStatusMeta.label}
                        </span>
                      )}
                      {inNegativeFlow && !recoveryPositiveStatus && availableNegativeSubStatuses.length === 0 && isLastNegativeOrderStatus(order.orderStatus, activeNegativeStatuses) && (
                        <span className="orderlist-status-completed" style={{ color: currentStatusMeta.color }}>
                          <Check size={14} aria-hidden /> {currentStatusMeta.label}
                        </span>
                      )}
                      {inNegativeFlow && availableNegativeSubStatuses.length > 0 && (
                        availableNegativeSubStatuses.map((subStatus) => {
                          const subMeta = getStatusMeta(subStatus.value, activeOrderStatuses, activeNegativeStatuses);
                          return (
                            <button
                              key={subStatus.value}
                              type="button"
                              className="orderlist-action-stepper-btn"
                              style={getStatusButtonStyles(subMeta.color)}
                              onClick={() => handleAdvanceStatus(subStatus.value)}
                              disabled={statusUpdating}
                            >
                              {statusUpdating ? 'Updating...' : `Set to ${subMeta.label}`}
                            </button>
                          );
                        })
                      )}
                      {!inNegativeFlow && menuNegativeStatuses.length > 0 && (
                        <div className="orderlist-negative-status-expand" ref={negativeMenuRef}>
                          <button
                            type="button"
                            className="orderlist-negative-status-trigger"
                            onClick={() => setNegativeMenuOpen((open) => !open)}
                            disabled={statusUpdating}
                            aria-expanded={negativeMenuOpen}
                          >
                            <span>{negativeGroupLabel}</span>
                            <ChevronDown size={16} className={negativeMenuOpen ? 'open' : ''} />
                          </button>
                          {negativeMenuOpen && (
                            <div className="orderlist-negative-status-expand-options">
                              {menuNegativeStatuses.map((negativeStatus) => {
                                const negativeMeta = getStatusMeta(negativeStatus.value, activeOrderStatuses, activeNegativeStatuses);
                                return (
                                  <button
                                    key={negativeStatus.value}
                                    type="button"
                                    className="orderlist-negative-status-option-btn"
                                    style={getStatusButtonStyles(negativeMeta.color)}
                                    onClick={() => {
                                      setNegativeMenuOpen(false);
                                      handleAdvanceStatus(negativeStatus.value);
                                    }}
                                    disabled={statusUpdating}
                                  >
                                    Set to {negativeMeta.label}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                      {isLinkedOrder && originalOrderMongoId && (
                        <button
                          type="button"
                          className="orderlist-go-to-order-btn"
                          onClick={() => onOpenRelatedOrder?.(originalOrderMongoId)}
                        >
                          Go to original order
                        </button>
                      )}
                      {canOfferLinkedOrders && (
                        <>
                          <button
                            type="button"
                            className="orderlist-create-return-btn"
                            onClick={() => openProductPicker('return')}
                            disabled={Boolean(existingReturn) || creatingReturn || statusUpdating}
                          >
                            Create Return Order
                          </button>
                          {existingReturn && (
                            <p className="orderlist-related-note">
                              Already have return order{' '}
                              <button
                                type="button"
                                className="orderlist-timeline-order-link"
                                onClick={() => onOpenRelatedOrder?.(existingReturn._id)}
                              >
                                #{existingReturn.orderId}
                              </button>
                            </p>
                          )}
                          <button
                            type="button"
                            className="orderlist-create-return-btn"
                            onClick={() => openProductPicker('replacement')}
                            disabled={Boolean(existingReplacement) || creatingReturn || statusUpdating}
                          >
                            Create Replacement
                          </button>
                          {existingReplacement && (
                            <p className="orderlist-related-note">
                              Already have replacement order{' '}
                              <button
                                type="button"
                                className="orderlist-timeline-order-link"
                                onClick={() => onOpenRelatedOrder?.(existingReplacement._id)}
                              >
                                #{existingReplacement.orderId}
                              </button>
                            </p>
                          )}
                        </>
                      )}
                      {canCancelDeliveredOrder && (
                        <button
                          type="button"
                          className="orderlist-negative-status-trigger"
                          onClick={() => handleAdvanceStatus('cancelled')}
                          disabled={statusUpdating}
                        >
                          Cancel order
                        </button>
                      )}
                      {canCancelRelated && (
                        <button
                          type="button"
                          className="orderlist-negative-status-trigger"
                          onClick={() => setCancelPromptOpen(true)}
                          disabled={statusUpdating}
                        >
                          {cancelRelatedIsReplacement ? 'Cancel replacement' : 'Cancel return'}
                        </button>
                      )}
                      {!isLinkedOrder && existingReturn && currentStatusValue === 'returned' && (
                        <button
                          type="button"
                          className="orderlist-type-badge return"
                          onClick={() => onOpenRelatedOrder?.(existingReturn._id)}
                        >
                          Return #{existingReturn.orderId}
                        </button>
                      )}
                      {!isLinkedOrder && existingReplacement && hasActiveReplacementOnOriginal && (
                        <button
                          type="button"
                          className="orderlist-type-badge replacement"
                          onClick={() => onOpenRelatedOrder?.(existingReplacement._id)}
                        >
                          Replacement #{existingReplacement.orderId}
                        </button>
                      )}
                      {!isLinkedOrder && cancelledRelatedOrders.map((item) => {
                        const childFlow = getFlowStatusesForOrder(item, order?.orderConfig || {});
                        const childStatusLabel = getStatusMeta(
                          item.orderStatus,
                          childFlow.orderStatuses,
                          childFlow.negativeOrderStatuses
                        ).label;
                        return (
                          <button
                            key={item._id}
                            type="button"
                            className="orderlist-type-badge replacement"
                            onClick={() => onOpenRelatedOrder?.(item._id)}
                            title={childStatusLabel}
                          >
                            Replacement #{item.orderId} (cancelled)
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                {(order.cancelReason || order.returnReason) && (
                  <div className="orderlist-status-reason-block">
                    {order.cancelReason && <p><strong>Cancel reason:</strong> {order.cancelReason}</p>}
                    {order.returnReason && <p><strong>Return reason:</strong> {order.returnReason}</p>}
                  </div>
                )}
              </div>
            </section>

            <section className="orderlist-details-card orderlist-order-note-card">
              <div className="orderlist-card-header-row">
                <h3>Order Note</h3>
                {canEdit && activeEditSection !== 'note' && (
                  <button type="button" className="orderlist-edit-toggle-btn" onClick={() => startSectionEdit('note')}>
                    <Pencil size={14} /> Edit
                  </button>
                )}
              </div>
              {activeEditSection === 'note' && formData ? (
                <div className="orderlist-section-edit">
                  <div className="orderlist-form-group">
                    <label htmlFor="section-orderNote">{formatFieldLabel('Order Note', false)}</label>
                    <textarea
                      id="section-orderNote"
                      name="orderNote"
                      value={formData.orderNote}
                      onChange={(e) => setFormData((prev) => ({ ...prev, orderNote: e.target.value }))}
                      rows={4}
                      disabled={sectionSaving}
                      placeholder="Add a note for this order..."
                    />
                  </div>
                  <div className="orderlist-section-edit-actions">
                    <button type="button" className="orderlist-submit-btn" onClick={saveSectionEdit} disabled={sectionSaving}>
                      {sectionSaving ? 'Saving...' : 'Save'}
                    </button>
                    <button type="button" className="orderlist-cancel-btn" onClick={cancelSectionEdit} disabled={sectionSaving}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : orderNoteText ? (
                <p className="orderlist-order-note-text">{orderNoteText}</p>
              ) : (
                <p className="orderlist-order-note-empty">No note added.</p>
              )}
            </section>

            <section className="orderlist-details-card orderlist-order-comments-card">
              <div className="orderlist-card-header-row">
                <h3>Comment</h3>
                {canEdit && activeEditSection !== 'comments' && (
                  <button type="button" className="orderlist-edit-toggle-btn" onClick={() => startSectionEdit('comments')}>
                    <Pencil size={14} /> Edit
                  </button>
                )}
              </div>
              {activeEditSection === 'comments' && formData ? (
                <div className="orderlist-section-edit">
                  <div className="orderlist-form-group">
                    <label htmlFor="section-comments">Comment</label>
                    <textarea
                      id="section-comments"
                      name="comments"
                      value={formData.comments}
                      onChange={(e) => setFormData((prev) => ({ ...prev, comments: e.target.value }))}
                      rows={4}
                      disabled={sectionSaving}
                      placeholder="Add a comment for this order..."
                    />
                  </div>
                  <div className="orderlist-section-edit-actions">
                    <button type="button" className="orderlist-submit-btn" onClick={saveSectionEdit} disabled={sectionSaving}>
                      {sectionSaving ? 'Saving...' : 'Save'}
                    </button>
                    <button type="button" className="orderlist-cancel-btn" onClick={cancelSectionEdit} disabled={sectionSaving}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : order.comments ? (
                <p className="orderlist-order-comments-text">{order.comments}</p>
              ) : (
                <p className="orderlist-order-comments-empty">No comment added.</p>
              )}
            </section>

            <section className="orderlist-details-card">
              <div className="orderlist-card-header-row">
                <h3>Delivery Details</h3>
                {canEdit && activeEditSection !== 'delivery' && (
                  <button type="button" className="orderlist-edit-toggle-btn" onClick={() => startSectionEdit('delivery')}>
                    <Pencil size={14} /> Edit
                  </button>
                )}
              </div>
              {activeEditSection === 'delivery' && formData ? (
                <div className="orderlist-section-edit">
                  <div className="orderlist-form-group">
                    <label htmlFor="section-deliveryDate">Delivery Date *</label>
                    <input
                      type="date"
                      id="section-deliveryDate"
                      value={formData.deliveryDate || ''}
                      min={formatDateOnly(new Date())}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData((prev) => ({ ...prev, deliveryDate: value, timeSlot: '', timeSlotId: '' }));
                        loadDeliverySlots(value);
                      }}
                      disabled={sectionSaving}
                    />
                  </div>
                  <div className="orderlist-form-group">
                    <label htmlFor="section-timeSlot">Time Slot *</label>
                    <select
                      id="section-timeSlot"
                      value={formData.timeSlot || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        const slot = deliverySlots.find((s) => s.name === value);
                        setFormData((prev) => ({
                          ...prev,
                          timeSlot: value,
                          timeSlotId: slot?._id && slot._id !== 'now' ? String(slot._id) : '',
                        }));
                      }}
                      disabled={sectionSaving || loadingDeliverySlots || !formData.deliveryDate}
                    >
                      <option value="">{loadingDeliverySlots ? 'Loading slots...' : (deliverySlots.length ? 'Select time slot' : 'No time slots configured')}</option>
                      {deliverySlots.map((slot) => (
                        <option key={slot._id || slot.name} value={slot.name}>{slot.name}</option>
                      ))}
                    </select>
                    {!loadingDeliverySlots && formData.deliveryDate && deliverySlots.length === 0 && (
                      <p className="orderlist-field-hint orderlist-field-hint-info">
                        No time slots configured. Add slots in admin settings.
                      </p>
                    )}
                  </div>
                  {usePriorityDelivery && (
                    <div className="orderlist-form-group orderlist-priority-delivery">
                      <label className="orderlist-checkbox-label" htmlFor="section-isPriorityDelivery">
                        <input
                          type="checkbox"
                          id="section-isPriorityDelivery"
                          checked={Boolean(formData.isPriorityDelivery)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setFormData((prev) => ({
                              ...prev,
                              isPriorityDelivery: checked,
                              priority_del_time: checked ? prev.priority_del_time : '',
                              priorityEndTime: checked ? prev.priorityEndTime : '',
                            }));
                          }}
                          disabled={sectionSaving}
                        />
                        Is priority delivery
                      </label>
                      {formData.isPriorityDelivery && (
                        <div className="orderlist-priority-time-row">
                          <label htmlFor="section-priorityMode">Priority time *</label>
                          <select
                            id="section-priorityMode"
                            value={PRIORITY_MODES.some((item) => item.value === formData.priorityMode) ? formData.priorityMode : 'before'}
                            onChange={(e) => setFormData((prev) => ({ ...prev, priorityMode: e.target.value }))}
                            disabled={sectionSaving}
                          >
                            {PRIORITY_MODES.map((item) => (
                              <option key={item.value} value={item.value}>{item.label}</option>
                            ))}
                          </select>
                          <div className="orderlist-priority-times">
                            <input
                              type="time"
                              id="section-priority_del_time"
                              value={formData.priority_del_time || ''}
                              onChange={(e) => setFormData((prev) => ({ ...prev, priority_del_time: e.target.value }))}
                              disabled={sectionSaving}
                              required
                            />
                            {formData.priorityMode === 'between' && (
                              <>
                                <span>to</span>
                                <input
                                  type="time"
                                  id="section-priorityEndTime"
                                  value={formData.priorityEndTime || ''}
                                  onChange={(e) => setFormData((prev) => ({ ...prev, priorityEndTime: e.target.value }))}
                                  disabled={sectionSaving}
                                  required
                                />
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="orderlist-section-edit-actions">
                    <button type="button" className="orderlist-submit-btn" onClick={saveSectionEdit} disabled={sectionSaving}>
                      {sectionSaving ? 'Saving...' : 'Save'}
                    </button>
                    <button type="button" className="orderlist-cancel-btn" onClick={cancelSectionEdit} disabled={sectionSaving}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p><strong>Delivery Date:</strong> {order.deliveryDateLabel || order.deliveryDate || '---'}</p>
                  <p><strong>Time Slot:</strong> {order.timeSlot || '---'}</p>
                  {usePriorityDelivery && (
                    formatPriorityBadgeLabel(order.priority_del_time) ? (
                      <p>
                        <strong>Priority:</strong>{' '}
                        <span className="orderlist-priority-badge">
                          {formatPriorityBadgeLabel(order.priority_del_time)}
                        </span>
                      </p>
                    ) : (
                      <p><strong>Priority:</strong> ---</p>
                    )
                  )}
                </>
              )}
            </section>

            <section className="orderlist-details-card">
              <div className="orderlist-card-header-row">
                <h3>Map Location</h3>
                {canEdit && canUpdateLocation && activeEditSection !== 'location' && (
                  <button type="button" className="orderlist-edit-toggle-btn" onClick={() => startSectionEdit('location')}>
                    <Pencil size={14} /> Edit
                  </button>
                )}
              </div>
              {activeEditSection === 'location' && formData ? (
                <div className="orderlist-section-edit">
                  <div className="orderlist-form-group">
                    <label htmlFor="order-map-link">Map URL</label>
                    <div className="orderlist-location-row-inline">
                      <input
                        type="text"
                        id="order-map-link"
                        value={mapLinkDraft}
                        onChange={(e) => applyEditedMapLink(e.target.value)}
                        placeholder="Paste a Google Maps link"
                        className="orderlist-map-link-input"
                      />
                      <button
                        type="button"
                        className="orderlist-map-btn"
                        onClick={() => setIsLocationPickerOpen(true)}
                      >
                        <MapPin size={16} /> Select on map
                      </button>
                    </div>
                    {resolvingMapLink && (
                      <p className="orderlist-order-note-empty">Resolving map link...</p>
                    )}
                    {formData.lat && formData.lng && (
                      <div className="orderlist-coords-below">
                        <Check className="orderlist-coords-success-icon" size={15} aria-hidden />
                        <span>{formData.lat}, {formData.lng}</span>
                      </div>
                    )}
                  </div>
                  <div className="orderlist-section-edit-actions">
                    <button type="button" className="orderlist-submit-btn" onClick={saveSectionEdit} disabled={sectionSaving || resolvingMapLink}>
                      {sectionSaving ? 'Saving...' : 'Save'}
                    </button>
                    <button type="button" className="orderlist-cancel-btn" onClick={cancelSectionEdit} disabled={sectionSaving}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : hasOrderLocation ? (
                <p>
                  <strong>Coordinates:</strong> {addr.lat || '-'}, {addr.long || addr.lng || '-'}
                  {' '}
                  <button type="button" className="orderlist-view-location-link" onClick={openOrderLocation}>
                    <MapPin size={14} /> View Location
                  </button>
                </p>
              ) : (
                <p className="orderlist-order-note-empty">No map location. Needed if this order is dispatched.</p>
              )}
            </section>

            {useCourierShipment && (shipmentFieldRows.length > 0 || canEdit) && (
              <section className="orderlist-details-card">
                <div className="orderlist-card-header-row">
                  <h3>Shipment</h3>
                  {canEdit && activeEditSection !== 'shipment' && (
                    <button type="button" className="orderlist-edit-toggle-btn" onClick={() => startSectionEdit('shipment')}>
                      <Pencil size={14} /> Edit
                    </button>
                  )}
                </div>
                {activeEditSection === 'shipment' && formData ? (
                  <div className="orderlist-section-edit">
                    <div className="orderlist-form-group">
                      <label htmlFor="section-shipmentNumber">Shipment ID</label>
                      <input
                        type="text"
                        id="section-shipmentNumber"
                        value={formData.customFields?.[SHIPMENT_FIELD_KEYS.number] || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            customFields: { ...(prev.customFields || {}), [SHIPMENT_FIELD_KEYS.number]: value },
                          }));
                        }}
                        disabled={sectionSaving}
                        placeholder="Enter shipment tracking number"
                      />
                    </div>
                    <div className="orderlist-form-group">
                      <label htmlFor="section-shipmentPartner">Courier Partner</label>
                      <select
                        id="section-shipmentPartner"
                        value={formData.customFields?.[SHIPMENT_FIELD_KEYS.partner] || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            customFields: { ...(prev.customFields || {}), [SHIPMENT_FIELD_KEYS.partner]: value },
                          }));
                        }}
                        disabled={sectionSaving}
                      >
                        <option value="">Select courier partner</option>
                        {SHIPMENT_PARTNER_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="orderlist-section-edit-actions">
                      <button type="button" className="orderlist-submit-btn" onClick={saveSectionEdit} disabled={sectionSaving}>
                        {sectionSaving ? 'Saving...' : 'Save'}
                      </button>
                      <button type="button" className="orderlist-cancel-btn" onClick={cancelSectionEdit} disabled={sectionSaving}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p>
                      <strong>Shipment ID:</strong> {order?.customFields?.[SHIPMENT_FIELD_KEYS.number] || '---'}
                    </p>
                    <p>
                      <strong>Courier Partner:</strong> {formatShipmentPartner(order?.customFields?.[SHIPMENT_FIELD_KEYS.partner])}
                    </p>
                    {useDelhiveryTracking && isDelhiveryPartner && (
                      <div className="orderlist-shipment-actions">
                        <button
                          type="button"
                          className="orderlist-delivery-details-btn"
                          onClick={openDeliveryTrackModal}
                          disabled={!shipmentId}
                          title={
                            shipmentId
                              ? 'View Delhivery tracking'
                              : 'Add a Shipment ID to view tracking'
                          }
                        >
                          Delivery Details
                        </button>
                      </div>
                    )}
                  </>
                )}
              </section>
            )}

            <section className="orderlist-details-card orderlist-delivery-staff-card">
              <h3>Delivery Staff</h3>

              <div className="orderlist-delivery-staff-section">
                <div className="orderlist-card-header-row orderlist-delivery-staff-header">
                  <p className="orderlist-delivery-staff-label">Delivery Driver</p>
                  {canEdit && activeEditSection !== 'driver' && (
                    <button type="button" className="orderlist-edit-toggle-btn" onClick={() => startSectionEdit('driver')}>
                      <Pencil size={14} /> Edit
                    </button>
                  )}
                </div>
                {activeEditSection === 'driver' && formData ? (
                  <div className="orderlist-section-edit">
                    <div className="orderlist-form-group">
                      <label htmlFor="section-driverId">Delivery Driver</label>
                      <select
                        id="section-driverId"
                        value={formData.driverId || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormData((prev) => ({ ...prev, driverId: value }));
                        }}
                        disabled={sectionSaving}
                      >
                        <option value="">Select driver</option>
                        {drivers.map((driver) => (
                          <option key={driver._id} value={driver._id}>{driver.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="orderlist-section-edit-actions">
                      <button type="button" className="orderlist-submit-btn" onClick={saveSectionEdit} disabled={sectionSaving}>
                        {sectionSaving ? 'Saving...' : 'Save'}
                      </button>
                      <button type="button" className="orderlist-cancel-btn" onClick={cancelSectionEdit} disabled={sectionSaving}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {driverName ? (
                      <div className="orderlist-staff-row orderlist-staff-row-delivery">
                        <Bike size={16} />
                        <span className="orderlist-staff-name">{driverName}</span>
                      </div>
                    ) : canAssignDriver ? (
                      <button
                        type="button"
                        className="orderlist-driver-assign-btn"
                        onClick={() => openDriverSelect('view')}
                      >
                        Assign Driver
                      </button>
                    ) : (
                      <p className="orderlist-order-note-empty">No driver assigned.</p>
                    )}
                    {order && normalizeStatusValue(order.orderStatus) !== 'cancelled' && (
                      <button
                        type="button"
                        className="orderlist-staff-share-btn"
                        onClick={openDriverShareModal}
                      >
                        Share via WhatsApp
                      </button>
                    )}
                  </>
                )}
              </div>

              {useDeliveryBoyShipment && (shipmentFieldRows.length > 0 || canEdit) && (
                <div className="orderlist-delivery-staff-section">
                  <div className="orderlist-card-header-row orderlist-delivery-staff-header">
                    <p className="orderlist-delivery-staff-label">Delivery Boy</p>
                    {canEdit && activeEditSection !== 'shipment' && (
                      <button type="button" className="orderlist-edit-toggle-btn" onClick={() => startSectionEdit('shipment')}>
                        <Pencil size={14} /> Edit
                      </button>
                    )}
                  </div>
                  {activeEditSection === 'shipment' && formData ? (
                    <div className="orderlist-section-edit">
                      <div className="orderlist-form-group">
                        <label htmlFor="section-shipmentPartner">Delivery Boy</label>
                        <select
                          id="section-shipmentPartner"
                          value={formData.customFields?.[SHIPMENT_FIELD_KEYS.partner] || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData((prev) => ({
                              ...prev,
                              customFields: { ...(prev.customFields || {}), [SHIPMENT_FIELD_KEYS.partner]: value },
                            }));
                          }}
                          disabled={sectionSaving}
                        >
                          <option value="">Select delivery boy</option>
                          {drivers.map((driver) => (
                            <option key={driver._id} value={driver.name}>{driver.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="orderlist-section-edit-actions">
                        <button type="button" className="orderlist-submit-btn" onClick={saveSectionEdit} disabled={sectionSaving}>
                          {sectionSaving ? 'Saving...' : 'Save'}
                        </button>
                        <button type="button" className="orderlist-cancel-btn" onClick={cancelSectionEdit} disabled={sectionSaving}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="orderlist-delivery-staff-value">
                      {formatShipmentPartner(order?.customFields?.[SHIPMENT_FIELD_KEYS.partner])}
                    </p>
                  )}
                </div>
              )}
            </section>

            <section className="orderlist-details-card">
              <div className="orderlist-card-header-row">
                <h3>Order Admin</h3>
                {canEdit && canViewAllOrders && activeEditSection !== 'admin' && (
                  <button type="button" className="orderlist-edit-toggle-btn" onClick={() => startSectionEdit('admin')}>
                    <Pencil size={14} /> Edit
                  </button>
                )}
              </div>
              {activeEditSection === 'admin' && formData ? (
                <div className="orderlist-section-edit">
                  <div className="orderlist-form-group">
                    <label htmlFor="section-adminId">Admin</label>
                    <select
                      id="section-adminId"
                      value={formData.adminId || ''}
                      onChange={(e) => setFormData((prev) => ({ ...prev, adminId: e.target.value }))}
                      disabled={sectionSaving || loadingAdmins}
                    >
                      <option value="">{loadingAdmins ? 'Loading admins...' : 'Select admin'}</option>
                      {admins.map((admin) => (
                        <option key={admin._id} value={admin._id}>{admin.name || admin.email}</option>
                      ))}
                    </select>
                  </div>
                  <div className="orderlist-section-edit-actions">
                    <button type="button" className="orderlist-submit-btn" onClick={saveSectionEdit} disabled={sectionSaving}>
                      {sectionSaving ? 'Saving...' : 'Save'}
                    </button>
                    <button type="button" className="orderlist-cancel-btn" onClick={cancelSectionEdit} disabled={sectionSaving}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <p><strong>Admin:</strong> {order.adminName || '---'}</p>
              )}
            </section>

            {headerCustomFieldRows.length > 0 && (
              <section className="orderlist-details-card orderlist-details-full">
                <div className="orderlist-card-header-row">
                  <h3>Custom Fields</h3>
                  {canEdit && activeEditSection !== 'custom' && (
                    <button type="button" className="orderlist-edit-toggle-btn" onClick={() => startSectionEdit('custom')}>
                      <Pencil size={14} /> Edit
                    </button>
                  )}
                </div>
                {activeEditSection === 'custom' && formData ? (
                  <div className="orderlist-section-edit">
                    {headerCustomFieldRows.map((field) => (
                      <div className="orderlist-form-group" key={field.key}>
                        <label htmlFor={`section-custom-${field.key}`}>
                          {formatFieldLabel(field.label, field.required)}
                        </label>
                        <input
                          id={`section-custom-${field.key}`}
                          value={formData.customFields?.[field.key] || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData((prev) => ({
                              ...prev,
                              customFields: { ...(prev.customFields || {}), [field.key]: value },
                            }));
                          }}
                          required={field.required}
                          disabled={sectionSaving}
                        />
                      </div>
                    ))}
                    <div className="orderlist-section-edit-actions">
                      <button type="button" className="orderlist-submit-btn" onClick={saveSectionEdit} disabled={sectionSaving}>
                        {sectionSaving ? 'Saving...' : 'Save'}
                      </button>
                      <button type="button" className="orderlist-cancel-btn" onClick={cancelSectionEdit} disabled={sectionSaving}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="orderlist-custom-fields-display">
                    {headerCustomFieldRows.map((field) => (
                      <p key={field.key}>
                        <strong>{field.label}:</strong> {field.value || '---'}
                      </p>
                    ))}
                  </div>
                )}
              </section>
            )}
            </div>

            <div className="orderlist-details-grid">
              <section className="orderlist-details-card">
                <div className="orderlist-card-header-row">
                  <h3>Customer</h3>
                  {canEdit && activeEditSection !== 'customer' && (
                    <button type="button" className="orderlist-edit-toggle-btn" onClick={() => startSectionEdit('customer')}>
                      <Pencil size={14} /> Edit
                    </button>
                  )}
                </div>
                {activeEditSection === 'customer' && formData ? (
                  <div className="orderlist-section-edit">
                    <div className="orderlist-form-group">
                      <label htmlFor="section-customerName">Customer Name</label>
                      <input id="section-customerName" name="customerName" value={formData.customerName} onChange={handleInputChange} />
                    </div>
                    <div className="orderlist-form-group">
                      <label htmlFor="section-customerPhone">Customer Phone</label>
                      <div className="orderlist-phone-input-wrapper">
                        <span className="orderlist-phone-prefix">{isIndia ? '+91' : '+974'}</span>
                        <input
                          type="tel"
                          id="section-customerPhone"
                          name="customerPhone"
                          value={formData.customerPhone}
                          onChange={handleInputChange}
                          onBlur={handlePhoneBlur}
                          maxLength={isIndia ? 10 : 8}
                        />
                      </div>
                    </div>
                    <div className="orderlist-form-group">
                      <label htmlFor="section-customerWhatsapp">{formatFieldLabel('WhatsApp Number', false)}</label>
                      <input
                        type="text"
                        id="section-customerWhatsapp"
                        name="customerWhatsapp"
                        value={formData.customerWhatsapp || ''}
                        onChange={handleInputChange}
                        placeholder="Optional — include country code if needed"
                        autoComplete="tel"
                      />
                    </div>
                    {isIndia ? (
                      <>
                        <div className="orderlist-form-group">
                          <label htmlFor="section-pincode">Pincode</label>
                          <input id="section-pincode" name="pincode" value={formData.pincode} onChange={handleInputChange} onBlur={handlePincodeBlur} maxLength={6} />
                        </div>
                        <div className="orderlist-form-group">
                          <label htmlFor="section-city">City / Area</label>
                          <input id="section-city" name="city" value={formData.city} onChange={handleInputChange} />
                        </div>
                      </>
                    ) : (
                      <div className="orderlist-form-group">
                        <label htmlFor="section-zone">Zone Number</label>
                        <input id="section-zone" name="zone" value={formData.zone} onChange={handleInputChange} />
                      </div>
                    )}
                    <div className="orderlist-form-group">
                      <label htmlFor="section-buildingNumber">Building / House No.</label>
                      <input
                        id="section-buildingNumber"
                        name="buildingNumber"
                        value={formData.buildingNumber || ''}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="orderlist-form-group">
                      <label htmlFor="section-apartment">Apartment</label>
                      <input
                        id="section-apartment"
                        name="apartment"
                        value={formData.apartment || ''}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="orderlist-form-group">
                      <label htmlFor="section-directionToReach">Direction to reach</label>
                      <input
                        id="section-directionToReach"
                        name="directionToReach"
                        value={formData.directionToReach || ''}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="orderlist-form-group">
                      <label htmlFor="section-address">Address</label>
                      <input id="section-address" name="address" value={formData.address} onChange={handleInputChange} />
                    </div>
                    <div className="orderlist-form-group orderlist-form-full">
                      <label>{formatFieldLabel('Delivery Location', false)}</label>
                      <div className="orderlist-location-row-inline">
                        <button
                          type="button"
                          className="orderlist-view-map-btn"
                          onClick={openMapUrl}
                          disabled={!formData.lat && !formData.lng && !formData.mapLink}
                        >
                          <Eye size={16} /> View Location
                        </button>
                        {canUpdateLocation && (
                          <button type="button" className="orderlist-map-btn" onClick={() => setIsLocationPickerOpen(true)}>
                            <MapPin size={16} /> Update Location
                          </button>
                        )}
                      </div>
                      <div className="orderlist-location-row-inline">
                        <input
                          type="text"
                          id="section-mapLink"
                          value={mapLinkDraft}
                          onChange={(e) => applyEditedMapLink(e.target.value)}
                          placeholder="Paste a Google Maps link"
                          className="orderlist-map-link-input"
                        />
                      </div>
                      {resolvingMapLink && (
                        <div className="orderlist-coords-below">
                          <span>Resolving map link...</span>
                        </div>
                      )}
                    </div>
                    <div className="orderlist-section-edit-actions">
                      <button type="button" className="orderlist-submit-btn" onClick={saveSectionEdit} disabled={sectionSaving}>
                        {sectionSaving ? 'Saving...' : 'Save'}
                      </button>
                      <button type="button" className="orderlist-cancel-btn" onClick={cancelSectionEdit} disabled={sectionSaving}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p><strong>{addr.receiversName || '-'}</strong></p>
                    <p>{addr.receiversPhoneNUmber || '-'}</p>
                    {addr.receiversWhatsappNumber ? (
                      <p>WhatsApp: {addr.receiversWhatsappNumber}</p>
                    ) : null}
                    {isIndia ? (
                      <>
                        <p>Pincode: {addr.pincode || '-'}</p>
                        {(addr.city || addr.district || addr.state) && (
                          <p>{[addr.city, addr.district, addr.state].filter(Boolean).join(', ')}</p>
                        )}
                      </>
                    ) : (
                      <p>Zone Number: {addr.zone || '-'}</p>
                    )}
                    <p>{[addr.buildingNumber || addr.houseNo, addr.apartment, addr.address].filter(Boolean).join(', ') || '-'}</p>
                    {addr.directionToReach && <p className="orderlist-details-muted">{addr.directionToReach}</p>}
                    {hasOrderLocation && (
                      <button type="button" className="orderlist-view-location-link" onClick={openOrderLocation}>
                        <MapPin size={14} />
                        View Location
                      </button>
                    )}
                  </>
                )}
              </section>

              <section className="orderlist-details-card">
                <div className="orderlist-card-header-row">
                  <h3>Billing</h3>
                  {(canEdit || canEditPaymentType) && activeEditSection !== 'billing' && (
                    <button type="button" className="orderlist-edit-toggle-btn" onClick={() => startSectionEdit('billing')}>
                      <Pencil size={14} /> Edit
                    </button>
                  )}
                </div>
                {activeEditSection === 'billing' && formData ? (
                  <div className="orderlist-section-edit">
                    <div className="orderlist-form-group">
                      <label htmlFor="section-totalPrice">{getSubTotalInputLabel(adminData)}</label>
                      <input
                        type="number"
                        step="0.01"
                        id="section-totalPrice"
                        name="totalPrice"
                        value={formData.totalPrice}
                        onChange={handleInputChange}
                        disabled={!canEdit || sectionSaving}
                      />
                    </div>
                    <div className="orderlist-form-group">
                      <label htmlFor="section-deliveryCharge">{getDeliveryChargeInputLabel(adminData)}</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        id="section-deliveryCharge"
                        name="deliveryCharge"
                        value={formData.deliveryCharge || ''}
                        onChange={handleInputChange}
                        placeholder="Optional"
                        disabled={!canEdit || sectionSaving}
                      />
                    </div>
                    <div className="orderlist-form-group">
                      <label htmlFor="section-discount">{getDiscountInputLabel(adminData)}</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        id="section-discount"
                        name="discount"
                        value={formData.discount || ''}
                        onChange={handleInputChange}
                        placeholder="Optional"
                        disabled={!canEdit || sectionSaving}
                      />
                    </div>
                    <p className="orderlist-payable-total">
                      Total Amount:{' '}
                      <strong>
                        {formatOrderTotal(
                          computePayableTotal({
                            subTotal: parseFloat(formData.totalPrice) || 0,
                            deliveryCharge: formData.deliveryCharge,
                            discount: formData.discount,
                          }),
                          adminData
                        )}
                      </strong>
                    </p>
                    <div className="orderlist-form-group">
                      <label htmlFor="section-paymentType">Payment Type</label>
                      <select
                        id="section-paymentType"
                        name="paymentType"
                        value={formData.paymentType}
                        onChange={handleInputChange}
                        disabled={!canEditPaymentType || sectionSaving}
                      >
                        {PAYMENT_TYPE_OPTIONS.map((opt) => (
                          <option key={opt.value || 'empty'} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    {formData.paymentType === 'advance_paid' && (
                      <div className="orderlist-form-group">
                        <label htmlFor="section-advancePaidAmount">Advance Amount *</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0.01"
                          id="section-advancePaidAmount"
                          name="advancePaidAmount"
                          value={formData.advancePaidAmount || ''}
                          onChange={handleInputChange}
                          placeholder="Advance amount paid"
                          disabled={!canEditPaymentType || sectionSaving}
                        />
                      </div>
                    )}
                    <div className="orderlist-section-edit-actions">
                      <button type="button" className="orderlist-submit-btn" onClick={saveSectionEdit} disabled={sectionSaving}>
                        {sectionSaving ? 'Saving...' : 'Save'}
                      </button>
                      <button type="button" className="orderlist-cancel-btn" onClick={cancelSectionEdit} disabled={sectionSaving}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="orderlist-details-advance-breakdown">
                      <div className="orderlist-details-advance-row">
                        <span>Sub Total</span>
                        <strong>{formatOrderTotal(getOrderSubTotal(order.bill), adminData)}</strong>
                      </div>
                      <div className="orderlist-details-advance-row">
                        <span>Delivery Charge</span>
                        <strong>{formatOrderTotal(order.bill?.deliveryCharge || 0, adminData)}</strong>
                      </div>
                      <div className="orderlist-details-advance-row">
                        <span>Discount</span>
                        <strong>{formatOrderTotal(order.bill?.discount || 0, adminData)}</strong>
                      </div>
                      <div className="orderlist-details-advance-row orderlist-details-balance-payment">
                        <span>Total Amount</span>
                        <strong>{formatOrderTotal(order.bill?.totalPrice, adminData)}</strong>
                      </div>
                    </div>
                    <p
                      className={`orderlist-details-payment ${
                        String(order.paymentType || '').toLowerCase() === 'cod'
                          ? 'orderlist-details-payment-cod'
                          : String(order.paymentType || '').toLowerCase() === 'prepaid'
                            ? 'orderlist-details-payment-online'
                            : String(order.paymentType || '').toLowerCase() === 'advance_paid'
                              ? 'orderlist-details-payment-advance'
                              : ''
                      }`}
                    >
                      {formatPaymentType(order.paymentType)}
                    </p>
                    {isAdvancePaidPayment(order.paymentType) && (
                      <div className="orderlist-details-advance-breakdown">
                        <div className="orderlist-details-advance-row">
                          <span>Total Amount</span>
                          <strong>{formatOrderTotal(order.bill?.totalPrice, adminData)}</strong>
                        </div>
                        <div className="orderlist-details-advance-row">
                          <span>Advance Payed</span>
                          <strong>{formatPrice(getAdvancePaidAmount(order), adminData)}</strong>
                        </div>
                        <div className="orderlist-details-advance-row orderlist-details-balance-payment">
                          <span>Balance Payment</span>
                          <strong>{formatPrice(getBalancePaymentAmount(order), adminData)}</strong>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </section>
            </div>

            {(order.products?.length > 0 || canEdit) && (
              <section className="orderlist-details-card orderlist-details-full">
                <div className="orderlist-details-products-header">
                  <h3 className="orderlist-details-products-title">Order Items</h3>
                  {canEdit && (
                    <button
                      type="button"
                      className="orderlist-details-products-add-btn"
                      onClick={() => {
                        prepareProductsForViewEdit();
                        setIsProductPickerOpen(true);
                      }}
                    >
                      {order.products?.length > 0 ? 'Add more' : 'Add products'}
                    </button>
                  )}
                </div>
                <div className="orderlist-details-products-list">
                  {(order.products || []).length === 0 && (
                    <p className="orderlist-no-products" style={{ margin: 0 }}>No products on this order yet.</p>
                  )}
                  {(order.products || []).map((p, idx) => {
                    const price = p.price ?? p.rate ?? 0;
                    const imageUrl = resolveOrderLineImage(p);
                    const variantLabel = formatOrderVariantLabel(p);
                    return (
                      <div key={orderLineKey(p, idx)} className="orderdetails-product-row">
                        <div className="orderdetails-product-img-wrapper">
                          {imageUrl ? (
                            <img src={imageUrl} alt={p.name} className="orderdetails-product-thumb" />
                          ) : (
                            <div className="orderdetails-product-thumb-fallback">
                              <ImageOff size={16} />
                            </div>
                          )}
                        </div>
                        <div className="orderdetails-product-code">
                          {typeof p.productId === 'number' ? p.productId : '-'}
                        </div>
                        <div className="orderdetails-product-main-info">
                          <div className="orderdetails-product-title-text">{p.name}</div>
                          {variantLabel ? (
                            <div className="orderdetails-product-variant-text">{variantLabel}</div>
                          ) : null}

                          <div className="orderdetails-product-meta-text">
                            {p.qty} × {formatOrderTotal(price, adminData)}
                          </div>
                        </div>
                        <div className="orderdetails-product-total-price">
                          {formatOrderTotal(p.qty * price, adminData)}
                        </div>
                        {canEdit && (
                          <button
                            type="button"
                            className="orderdetails-product-edit-icon-btn"
                            onClick={() => {
                              prepareProductsForViewEdit();
                              setEditingProduct({
                                ...p,
                                _id: p._id,
                                productId: p.productId,
                              });
                              setEditProductQty(p.qty);
                            }}
                            title="Edit products"
                          >
                            <Pencil size={15} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {order.timeline?.length > 0 && (
              <section className="orderlist-details-card orderlist-details-full">
                <h3>Order Timeline</h3>
                <div className="orderlist-timeline">
                  {order.timeline.map((item, index) => (
                    <div key={index} className="orderlist-timeline-item">
                      <div className="orderlist-timeline-marker">
                        <div className="orderlist-timeline-dot" />
                        {index !== order.timeline.length - 1 && <div className="orderlist-timeline-line" />}
                      </div>
                      <div className="orderlist-timeline-content">
                        <div className="orderlist-timeline-label">
                          {item.label}
                          {item.originalOrderMongoId && (
                            <button
                              type="button"
                              className="orderlist-timeline-order-link"
                              onClick={() => onOpenRelatedOrder?.(item.originalOrderMongoId)}
                            >
                              #{item.originalOrderId}
                            </button>
                          )}
                          {item.relatedOrderMongoId && (
                            <button
                              type="button"
                              className="orderlist-timeline-order-link"
                              onClick={() => onOpenRelatedOrder?.(item.relatedOrderMongoId)}
                            >
                              #{item.relatedOrderId}
                            </button>
                          )}
                        </div>
                        <div className="orderlist-timeline-time">{item.time || '-'}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        ) : null}

        <ProductPickerModal
          isOpen={isProductPickerOpen}
          onClose={() => setIsProductPickerOpen(false)}
          onSave={editMode ? setSelectedProducts : handleSaveProductsDirectly}
          initialSelected={selectedProducts}
          adminData={adminData}
        />

        {editingProduct && (
          <div className="orderdetails-edit-item-modal-overlay" onClick={() => setEditingProduct(null)}>
            <div className="orderdetails-edit-item-modal" onClick={(e) => e.stopPropagation()}>
              <div className="orderdetails-edit-item-header">
                <h3>Edit Item</h3>
                <button type="button" className="orderdetails-edit-item-close" onClick={() => setEditingProduct(null)}>
                  &times;
                </button>
              </div>
              <div className="orderdetails-edit-item-body">
                <div className="orderdetails-edit-item-info">
                  <div className="orderdetails-edit-item-name">{editingProduct.name}</div>
                  <div className="orderdetails-edit-item-total">
                    {formatOrderTotal((editingProduct.price || editingProduct.rate || 0) * editProductQty, adminData)}
                  </div>
                </div>
                <div className="orderdetails-edit-item-form-group">
                  <label>Quantity</label>
                  <div className="orderdetails-edit-item-qty-selector">
                    <button type="button" className="orderdetails-edit-item-qty-btn" onClick={() => setEditProductQty((prev) => Math.max(1, prev - 1))}>-</button>
                    <span className="orderdetails-edit-item-qty-display">{editProductQty}</span>
                    <button type="button" className="orderdetails-edit-item-qty-btn" onClick={() => setEditProductQty((prev) => prev + 1)}>+</button>
                  </div>
                </div>
                <div className="orderdetails-edit-item-actions">
                  <button
                    type="button"
                    className="orderdetails-edit-item-update-btn"
                    onClick={async () => {
                      const editKey = editingProduct._id + (editingProduct.selectedVariant?.sku ? '-' + editingProduct.selectedVariant.sku : '');
                      const updatedProducts = selectedProducts.map((item) => {
                        const itemKey = item._id + (item.selectedVariant?.sku ? '-' + item.selectedVariant.sku : '');
                        return itemKey === editKey ? { ...item, qty: editProductQty } : item;
                      });
                      setEditingProduct(null);
                      await handleSaveProductsDirectly(updatedProducts);
                    }}
                  >
                    Update Quantity
                  </button>
                  <button
                    type="button"
                    className="orderdetails-edit-item-delete-btn"
                    onClick={async () => {
                      const isStandardOrder = String(order?.type || 'order').toLowerCase() === 'order';
                      if (productsRequiredForStore(adminData) && isStandardOrder && selectedProducts.length <= 1) {
                        toast.error('Order must have at least one product');
                        return;
                      }
                      const editKey = editingProduct._id + (editingProduct.selectedVariant?.sku ? '-' + editingProduct.selectedVariant.sku : '');
                      const updatedProducts = selectedProducts.filter((item) => {
                        const itemKey = item._id + (item.selectedVariant?.sku ? '-' + item.selectedVariant.sku : '');
                        return itemKey !== editKey;
                      });
                      setEditingProduct(null);
                      await handleSaveProductsDirectly(updatedProducts, 'Product deleted successfully');
                    }}
                  >
                    Delete Item
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {isViewProductsOpen && (
          <div className="orderlist-sel-modal-overlay" onClick={() => setIsViewProductsOpen(false)}>
            <div className="orderlist-sel-products-modal" onClick={(e) => e.stopPropagation()}>
              <div className="orderlist-sel-modal-header">
                <h2 className="orderlist-sel-modal-header-title">Selected Products</h2>
                <button type="button" className="orderlist-sel-modal-close-x" onClick={() => setIsViewProductsOpen(false)}>&times;</button>
              </div>
              <div className="orderlist-sel-modal-body">
                {selectedProducts.length > 0 ? (
                  <div className="orderlist-selected-products-table-wrapper" style={{ maxHeight: 'none', margin: 0 }}>
                    <table className="orderlist-selected-products-table">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Price</th>
                          <th>Qty</th>
                          <th>Total</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {selectedProducts.map((p, idx) => (
                          <tr key={`${p._id || idx}-${p.selectedVariant?.sku || ''}`}>
                            <td>
                              <div className="orderlist-table-product-info">
                                {p.images?.[0]?.thumbnail || p.images?.[0]?.image ? (
                                  <img src={p.images?.[0]?.thumbnail || p.images?.[0]?.image} alt={p.name} className="orderlist-table-product-thumb" />
                                ) : (
                                  <div className="orderlist-table-product-thumb-fallback"><ImageOff size={14} /></div>
                                )}
                                <div className="orderlist-table-product-name-wrapper">
                                  <span className="orderlist-table-product-name">{p.name}</span>
                                </div>
                              </div>
                            </td>
                            <td>{formatOrderTotal(p.price, adminData)}</td>
                            <td>
                              <div className="orderlist-table-qty-selector">
                                <button type="button" className="orderlist-table-qty-btn" onClick={() => handleUpdateSelectedQty(p._id, p.selectedVariant?.sku, p.qty - 1)}>-</button>
                                <input type="number" min="1" value={p.qty} onChange={(e) => handleUpdateSelectedQty(p._id, p.selectedVariant?.sku, parseInt(e.target.value, 10) || 1)} className="orderlist-table-qty-input" />
                                <button type="button" className="orderlist-table-qty-btn" onClick={() => handleUpdateSelectedQty(p._id, p.selectedVariant?.sku, p.qty + 1)}>+</button>
                              </div>
                            </td>
                            <td>{formatOrderTotal(p.qty * p.price, adminData)}</td>
                            <td>
                              <button type="button" className="orderlist-remove-product-btn" onClick={() => setSelectedProducts((prev) => prev.filter((item) => !(item._id === p._id && (item.selectedVariant?.sku || null) === (p.selectedVariant?.sku || null))))}>
                                <Trash2 size={15} />
                              </button>
                            </td>
                          </tr>
                        ))}                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="orderlist-no-products" style={{ margin: 0 }}>No products selected yet.</div>
                )}
              </div>
              <div className="orderlist-sel-modal-footer">
                <div style={{ fontWeight: '600', color: '#1e293b' }}>
                  Total: {formatOrderTotal(itemTotal, adminData)}
                </div>
              </div>
            </div>
          </div>
        )}

        <DriverSelectModal
          isOpen={showDriverSelect}
          onClose={closeDriverSelect}
          drivers={drivers}
          selectedDriverId={driverSelectMode === 'edit' ? formData?.driverId : currentDriverId}
          onSelect={handleDriverSelect}
          loading={driverActionLoading}
          title={
            driverSelectMode === 'share'
              ? 'Share via WhatsApp'
              : (driverName || formData?.driverId ? 'Select Driver' : 'Assign Driver')
          }
          mode={driverSelectMode === 'share' ? 'share' : 'select'}
          onShareOther={handleShareRiderToOther}
        />

        <PincodeSelectModal
          isOpen={isPincodeModalOpen}
          onClose={() => setIsPincodeModalOpen(false)}
          options={pincodeOptions}
          onSelect={(address) => setFormData((prev) => ({ ...prev, ...address }))}
        />

        {useDispatch && showRouteEditModal && routeEntry && (
          <DispatchEditModal
            isOpen={showRouteEditModal}
            onClose={() => setShowRouteEditModal(false)}
            route={routeEntry}
            drivers={routeDrivers}
            storeCoordinates={routeStoreCoordinates}
            onSave={async (updatedRoute) => {
              const response = await dispatchAPI.updateRouteInHistory(
                updatedRoute._id || updatedRoute.id,
                updatedRoute.plans,
                updatedRoute.slot,
                updatedRoute.date,
                updatedRoute.slotId
              );
              if (response.success) {
                await loadOrder({ silent: true });
                onUpdated?.();
                return true;
              }
              toast.error(response.msg || 'Failed to update route');
              return false;
            }}
          />
        )}

      </div>

      {shippedModal.isOpen && (
        <div
          className="orderlist-confirm-overlay"
          onClick={(e) => {
            e.stopPropagation();
            if (!statusUpdating) {
              setShippedModal({ isOpen: false, shipmentNumber: '', shipmentPartner: '' });
            }
          }}
        >
          <div className="orderlist-confirm-dialog orderlist-shipment-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="orderlist-confirm-header">
              <h3>Mark as Shipped</h3>
              <button
                type="button"
                className="orderlist-confirm-close-btn"
                onClick={() => setShippedModal({ isOpen: false, shipmentNumber: '', shipmentPartner: '' })}
                disabled={statusUpdating}
              >
                <X size={18} />
              </button>
            </div>
            <div className="orderlist-section-edit">
              {useCourierShipment && (
                <div className="orderlist-form-group">
                  <label htmlFor="shipment-number">Shipment Number *</label>
                  <input
                    id="shipment-number"
                    value={shippedModal.shipmentNumber}
                    onChange={(e) => setShippedModal((prev) => ({ ...prev, shipmentNumber: e.target.value }))}
                    placeholder="Enter shipment tracking number"
                    disabled={statusUpdating}
                  />
                </div>
              )}
              <div className="orderlist-form-group">
                <label htmlFor="shipment-partner">
                  {useCourierShipment ? 'Courier Partner *' : 'Delivery Boy *'}
                </label>
                {useCourierShipment ? (
                  <select
                    id="shipment-partner"
                    value={shippedModal.shipmentPartner}
                    onChange={(e) => setShippedModal((prev) => ({ ...prev, shipmentPartner: e.target.value }))}
                    disabled={statusUpdating}
                  >
                    <option value="">Select partner</option>
                    {SHIPMENT_PARTNER_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                ) : (
                  <select
                    id="shipment-partner"
                    value={shippedModal.shipmentPartner}
                    onChange={(e) => setShippedModal((prev) => ({ ...prev, shipmentPartner: e.target.value }))}
                    disabled={statusUpdating}
                  >
                    <option value="">Select delivery boy</option>
                    {drivers.map((driver) => (
                      <option key={driver._id} value={driver.name}>{driver.name}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>
            <div className="orderlist-confirm-actions">
              <button
                type="button"
                className="orderlist-cancel-btn"
                onClick={() => setShippedModal({ isOpen: false, shipmentNumber: '', shipmentPartner: '' })}
                disabled={statusUpdating}
              >
                Cancel
              </button>
              <button
                type="button"
                className="orderlist-submit-btn"
                onClick={handleShippedSubmit}
                disabled={statusUpdating}
              >
                {statusUpdating ? 'Processing...' : 'Confirm Shipped'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deliveryTrackModal.isOpen && (
        <div
          className="orderlist-confirm-overlay"
          onClick={(e) => {
            e.stopPropagation();
            if (!deliveryTrackModal.loading) closeDeliveryTrackModal();
          }}
        >
          <div
            className="orderlist-confirm-dialog orderlist-delivery-track-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="orderlist-confirm-header">
              <h3>Delivery Details</h3>
              <button
                type="button"
                className="orderlist-confirm-close-btn"
                onClick={closeDeliveryTrackModal}
                disabled={deliveryTrackModal.loading}
              >
                <X size={18} />
              </button>
            </div>

            {deliveryTrackModal.loading ? (
              <p className="orderlist-delivery-track-loading">Fetching tracking details...</p>
            ) : deliveryTrackModal.error ? (
              <p className="orderlist-delivery-track-error">{deliveryTrackModal.error}</p>
            ) : (
              <>
                <div className="orderlist-delivery-track-summary">
                  <p><strong>AWB:</strong> {deliveryTrackModal.data?.waybill || shipmentId}</p>
                  {deliveryTrackModal.data?.status ? (
                    <p><strong>Status:</strong> {deliveryTrackModal.data.status}</p>
                  ) : null}
                  {deliveryTrackModal.data?.statusLocation ? (
                    <p><strong>Location:</strong> {deliveryTrackModal.data.statusLocation}</p>
                  ) : null}
                  {deliveryTrackModal.data?.statusDateTime ? (
                    <p><strong>Updated:</strong> {deliveryTrackModal.data.statusDateTime}</p>
                  ) : null}
                </div>

                <h4 className="orderlist-delivery-track-heading">Scan Timeline</h4>
                {deliveryTrackModal.data?.scans?.length > 0 ? (
                  <div className="orderlist-timeline orderlist-delivery-track-timeline">
                    {deliveryTrackModal.data.scans.map((item, index) => (
                      <div key={`${item.timeRaw || item.time}-${index}`} className="orderlist-timeline-item">
                        <div className="orderlist-timeline-marker">
                          <div className="orderlist-timeline-dot" />
                          {index !== deliveryTrackModal.data.scans.length - 1 && (
                            <div className="orderlist-timeline-line" />
                          )}
                        </div>
                        <div className="orderlist-timeline-content">
                          <div className="orderlist-timeline-label">{item.label}</div>
                          {item.location ? (
                            <div className="orderlist-timeline-location">{item.location}</div>
                          ) : null}
                          <div className="orderlist-timeline-time">{item.time || '-'}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="orderlist-delivery-track-empty">No scan details available yet.</p>
                )}
              </>
            )}

            <div className="orderlist-form-actions">
              <button
                type="button"
                className="orderlist-cancel-btn"
                onClick={closeDeliveryTrackModal}
                disabled={deliveryTrackModal.loading}
              >
                Close
              </button>
              <button
                type="button"
                className="orderlist-submit-btn"
                onClick={openDeliveryTrackModal}
                disabled={deliveryTrackModal.loading || !canTrackDelhivery}
              >
                {deliveryTrackModal.loading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>
      )}

      {statusConfirm.isOpen && (
        <div
          className="orderlist-confirm-overlay"
          onClick={(e) => {
            e.stopPropagation();
            if (!statusUpdating) {
              setStatusConfirm({ isOpen: false, status: null, title: '', message: '', reason: '' });
            }
          }}
        >
          <div className="orderlist-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="orderlist-confirm-header">
              <h3>{statusConfirm.title}</h3>
              <button
                type="button"
                className="orderlist-confirm-close-btn"
                onClick={() => setStatusConfirm({ isOpen: false, status: null, title: '', message: '', reason: '' })}
                disabled={statusUpdating}
              >
                <X size={18} />
              </button>
            </div>
            <p>{statusConfirm.message}</p>
            {requiresStatusReason(statusConfirm.status) && (
              <div className="orderlist-form-group">
                <label htmlFor="statusReason">
                  {getStatusReasonField(statusConfirm.status) === 'returnReason' ? 'Return reason (optional)' : 'Cancel reason (optional)'}
                </label>
                <textarea
                  id="statusReason"
                  value={statusConfirm.reason}
                  onChange={(e) => setStatusConfirm((prev) => ({ ...prev, reason: e.target.value }))}
                  rows={3}
                  placeholder="Enter a reason"
                  disabled={statusUpdating}
                />
              </div>
            )}
            <div className="orderlist-confirm-actions">
              <button
                type="button"
                className="orderlist-cancel-btn"
                onClick={() => setStatusConfirm({ isOpen: false, status: null, title: '', message: '', reason: '' })}
                disabled={statusUpdating}
              >
                Cancel
              </button>
              <button
                type="button"
                className="orderlist-submit-btn"
                onClick={() => handleAdvanceStatus(statusConfirm.status, true, statusConfirm.reason)}
                disabled={statusUpdating}
              >
                {statusUpdating ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isLocationPickerOpen && (
        <LocationPickerModal
          onClose={() => setIsLocationPickerOpen(false)}
          isIndia={isIndia}
          initial={formData?.lat && formData?.lng
            ? { lat: formData.lat, lng: formData.lng, placeName: formData.placeName }
            : { lat: addr.lat, lng: addr.long || addr.lng, placeName: addr.address }}
          onConfirm={async (location) => {
            if (editMode || activeEditSection === 'location' || activeEditSection === 'customer') {
              handleLocationPicked(location);
              return;
            }
            const result = await ordersAPI.updateOrder(orderId, {
              lat: location.lat,
              lng: location.lng,
              mapLink: location.mapLink || '',
            });
            if (result.success) {
              setOrder(result.data);
              toast.success('Map location saved');
              onUpdated?.();
            } else {
              toast.error(result.error || 'Failed to save location');
            }
          }}
        />
      )}

      {productPicker && (
        <div className="orderlist-confirm-overlay" onClick={() => !creatingReturn && setProductPicker(null)}>
          <div className="orderlist-confirm-dialog orderlist-return-picker-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>{productPicker.kind === 'replacement' ? 'Create replacement' : 'Create return order'}</h3>
            <p>Select the products to include.</p>
            <div className="orderlist-return-product-list">
              {productPicker.lines.map((line, index) => (
                <label key={line._id || index} className="orderlist-return-product-row">
                  <input
                    type="checkbox"
                    checked={line.selected}
                    onChange={(e) => {
                      const selected = e.target.checked;
                      setProductPicker((prev) => ({
                        ...prev,
                        lines: prev.lines.map((item, itemIndex) => (
                          itemIndex === index ? { ...item, selected } : item
                        )),
                      }));
                    }}
                  />
                  <span className="orderlist-return-product-name">{line.name}</span>
                  <span>{formatOrderTotal(line.price, adminData)}</span>
                  <input
                    type="number"
                    min="1"
                    max={line.maxQty}
                    value={line.qty}
                    disabled={!line.selected}
                    onChange={(e) => {
                      const qty = Math.max(1, Math.min(line.maxQty, Number(e.target.value) || 1));
                      setProductPicker((prev) => ({
                        ...prev,
                        lines: prev.lines.map((item, itemIndex) => (
                          itemIndex === index ? { ...item, qty } : item
                        )),
                      }));
                    }}
                  />
                </label>
              ))}
            </div>
            <div className="orderlist-confirm-actions">
              <button type="button" className="orderlist-cancel-btn" onClick={() => setProductPicker(null)} disabled={creatingReturn}>
                Cancel
              </button>
              <button type="button" className="orderlist-submit-btn" onClick={submitProductPicker} disabled={creatingReturn}>
                {creatingReturn ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {cancelPromptOpen && (
        <div className="orderlist-confirm-overlay" onClick={() => !statusUpdating && setCancelPromptOpen(false)}>
          <div className="orderlist-confirm-dialog orderlist-return-picker-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>{cancelRelatedIsReplacement ? 'Cancel replacement' : 'Cancel return'}</h3>
            <p>
              {cancelRelatedIsReplacement && currentStatusValue === 'delivered'
                ? 'Cancel the linked replacement order. This original order stays delivered unless you choose Cancelled below.'
                : 'Set this order to delivered or cancelled.'}
              {' '}
              The linked {cancelRelatedIsReplacement ? 'replacement' : 'return'} order will
              {cancelRelatedIsReplacement ? ' be marked replacement cancelled' : ' be removed'}.
            </p>
            <div className="orderlist-confirm-actions">
              <button type="button" className="orderlist-submit-btn" onClick={() => confirmCancelRelated('delivered')} disabled={statusUpdating}>
                {cancelRelatedIsReplacement && currentStatusValue === 'delivered' ? 'Keep delivered' : 'Delivered'}
              </button>
              <button type="button" className="orderlist-negative-status-trigger" onClick={() => confirmCancelRelated('cancelled')} disabled={statusUpdating}>
                Cancelled
              </button>
              <button type="button" className="orderlist-cancel-btn" onClick={() => setCancelPromptOpen(false)} disabled={statusUpdating}>
                Back
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetailsModal;
